/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * PostgreSQL dialect-native resolver for the `pgsql` engine (PRD Slice
 * "PostgreSQL Resolver — Core").
 *
 * Pipeline mirrors `mssqlResolver.ts` (pure-text after GRA-7 Slice A4 — no
 * tree-sitter parse in the resolver hot path):
 *   1. Derive `CompletionContext` from source text (statement isolation +
 *      paren-frame state machine + dotted-base extractor + alias/CTE
 *      bindings).
 *   2. Bail when inside a string or comment.
 *   3. Build `CompletionCandidate[]` from:
 *        - alias-dot column expansion (when the dotted base resolves to a
 *          bound alias) — columns come straight from the bus snapshot
 *          because the PG cache already carries them inline (no separate
 *          column cache needed),
 *        - schema-qualified table list (when the base resolves to a schema),
 *        - position-aware columns + tables + schemas + CTEs + keywords,
 *        - PG built-in functions from the curated `pg_catalog` list at
 *          tier 8 (always included so unqualified function lookup falls
 *          back to pg_catalog per PG's implicit search behaviour).
 *   4. Rank via `rankCandidates` with the keyword-casing hint.
 *   5. Translate each `MonacoCompletionItem` to Monaco's runtime shape.
 *
 * Differences from MSSQL:
 *   - Identifier folding: unquoted → lower (PG default), `"Camel"` preserves.
 *   - Bus snapshot tables already carry columns → no per-table column cache.
 *   - `search_path` (lower-cased, $user-resolved) is stored on the snapshot.
 *     Unqualified relation matches walk schemas in search_path order; the
 *     first match wins. Schemas not in search_path still surface but tier-2
 *     candidates from in-search_path schemas sort before tier-2 candidates
 *     from out-of-path schemas (RESEARCH.md §5.2 anti-SOUNDEX).
 *   - `pg_catalog` functions surface at tier 8 (system-function) so common
 *     built-ins like `now`, `coalesce`, `length` always appear without
 *     explicit qualification.
 *
 * Pure module — no Monaco/React imports at module-level. The `monaco`
 * runtime instance flows through resolver input.
 */

import type {
  AliasBinding,
  ColumnMeta,
  CompletionCandidate,
  CompletionPositionKind,
  CteBinding,
  DatabaseEngineId,
  FkEdge,
  TableMeta, CompletionCandidateTier } from '@/core/engines';
import { ensurePgSchema } from '@/features/connections/services/language-services/pgSchemaCache';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { deriveCompletionContext } from '@/features/intellisense/context/deriveContext';
import { extractDottedBase } from '@/features/intellisense/context/dottedBase';
import { applyGroupByScopeFilter } from '@/features/intellisense/context/groupByScope';
import { isAfterCompletedFromRelation } from '@/features/intellisense/context/isAfterCompletedFromRelation';
import { isAfterCompletedSelectProjection } from '@/features/intellisense/context/isAfterCompletedSelectProjection';
import { scanOptionsFor } from '@/features/intellisense/context/slotTextScan';
import { applyParenScopeFilter } from './parenScopeFilter';
import { applyIndexTargetFilter } from './indexTargetFilter';
import { emitIndexNameCandidates } from './indexNameSlot';
import { filterAlreadyAssignedSetColumns } from './filterAssignedSetColumns';
import { emitAggregateArgHints, emitAggregateTailHints } from './aggregateArgHints';
import { emitProjectionWildcard } from './projectionWildcard';
import { emitSavepointNameCandidates } from '../context/savepointSlot';
import { isAtRenameTargetSlot } from '../context/renameTargetSlot';
import { emitTriggerPseudoRelationNames } from '../resolver/pseudoTables';
import { isAfterCompletedListItem } from '@/features/intellisense/context/nextKeywordSlot';
import { inferSubqueryColumns } from '@/features/intellisense/resolver/inferSubqueryColumns';
import { emitNamedWindowCandidates } from '@/features/intellisense/resolver/inferNamedWindows';
import { resolveCteColumns } from '@/features/intellisense/resolver/resolveCteColumns';
import { emitSelectListAliasCandidates } from '@/features/intellisense/resolver/selectListAliases';
import {
  emitMergeValuesQualifiedColumns,
  isAtMergeValuesElement,
  partitionMergeBindings,
} from '@/features/intellisense/resolver/mergeValuesQualified';
import {
  generateFkJoinSnippet,
  makeApplicableFksFinder,
} from '@/features/intellisense/fkJoin/fkJoinSnippet';
import { makeFkEndpointMatcher } from '@/features/intellisense/fkJoin/fkEndpointMatch';
import {
  computeBufferReferencedTableKeys,
  computeFkLinkedTableKeys,
  fkLinkedTableKey,
  unionTableKeys,
} from '@/features/intellisense/fkJoin/fkLinkedTableKeys';
import { promoteCaretBindingRelation } from '@/features/intellisense/fkJoin/caretBindingPromotion';
import { rankCandidates, buildRankOptions } from '@/features/intellisense/ranking/rank';
import { emitAliasCandidate } from './aliasSuggestions';
import { enrichCandidatesWithDocs } from './completionDocs';
import { toMonacoItem } from './monacoItem';

import type {
  OrchestratorResolver,
  OrchestratorResolverInput,
  OrchestratorResolverResult,
} from '../orchestrator/types';

import {
  emitCreateObjectNamespaces,
  isCreateIndexNameSlot,
  isCreateTriggerNameSlot,
} from './createObjectNamespaces';
import {
  doubleQuote,
  foldIdentifier,
  formatIdentifier,
  formatQualifiedTwoPart,
  needsDoubleQuote,
  resolvePgRelation,
  type PgSearchPath,
  unquoteIdentifier,
} from './pgsqlIdentifiers';
import { getPgsqlKeywordsForPosition, PGSQL_KEYWORDS } from './pgsqlKeywords';
import {
  extractInProgressCreateTableColumns,
  findTriggerOnTableAhead,
  isInsideTableDdlStatement,
  type InProgressColumn,
  scanBufferCreatedRoutines,
  scanBufferCreatedTypes,
  type InBufferType,
} from './ddlStatementScan';
import {
  findPgsqlQualifiedTypeSlotSchema,
  getPgsqlDataTypes,
  isPgsqlDataTypePosition,
  isPgsqlReturnsTypeSlot,
  PGSQL_RETURNS_ONLY_TYPES,
} from './pgsqlDataTypes';
import { getPgBuiltinFunctions, getPgSetReturningFunctions } from './pgsqlBuiltinFunctions';
import { findProceduralForInBindings } from './oracleForInBindings';
import {
  findEnclosingPlpgsqlBlock,
  findInsertOnConflictTarget,
  findPgDateFieldLiteralSlot,
  findPgRangeOptionValueSlot,
  getPgDateFields,
  getPgvectorOperators,
  isPgsqlExecuteFunctionDrill,
  isPgsqlIndexPredicate,
  isPgsqlRaiseArgument,
  isPgsqlRelationOnlyDrill,
  PG_RANGE_CANONICAL_FUNCTIONS,
  PG_RANGE_SUBTYPE_DIFF_FUNCTIONS,
  type PgsqlInsertTarget,
  type PlpgsqlVariable,
} from './pgsqlExtras';
import {
  emitPgSequenceCandidates,
  findPgSequenceLiteralSlot,
} from './pgsqlSequenceSlot';
import {
  classifyPgRelationDrillKind,
  scanBufferCreatedMatviews,
  type PgDrillRelationKind,
} from './pgsqlRelationDrill';
import { demoteGeneratedInsertColumns } from './insertableColumnDemotion';
import { emitPgsqlProceduralLocalCandidates } from './pgsqlProceduralLocals';
import { emitPredicateHeadCandidates } from '../predicateHead/emitPredicateHeadCandidates';
import { emitValueSlotCandidates } from '../valueSlot/emitValueSlotCandidates';
import { buildPgsqlValueSlotAdapter, collectPgsqlScalarRoutineDescriptors } from './pgsqlValueSlotAdapter';
import {
  emitResolvedValueCandidates,
  legalValueKeywordsFor,
  type ResolvedValueTarget,
} from '../valueSlot/emitResolvedValueCandidates';
import { coarseTypeOfDataType } from '../valueSlot/types';
import { findInsertValuesTarget } from '../context/findInsertValuesTarget';
import { findInsertReturningTarget } from '../context/findInsertReturningTarget';
import { emitTypeAwareOperators, emitTypeAwareExpressionOperators } from '../operators/emitJsonOperatorCandidates';
import { findPrecedingExpressionType } from '../operators/findPrecedingExpressionType';
import { makeRoutineReturnResolver } from '../operators/routineReturnType';
import {
  emitJsonPathCandidates,
  findJsonPathStringPosition,
} from '../operators/emitJsonPathCandidates';
import { findPrecedingColumnOperand } from '../operators/findPrecedingColumnOperand';
import { emitEnumValueCandidates } from '../operators/emitEnumValueCandidates';
import { computeEnumValueEdit } from '../operators/enumValueInsertion';
import { detectComparisonValueContext } from '../context/detectComparisonValueContext';
import { findValueStringBodyStart } from '../context/comparisonScanHelpers';
import { findTypedInsertColumns } from '../context/findTypedInsertColumns';
import { isAtCallStmtRoutineRef } from '../context/isAtCallStmtRoutineRef';
import { extractPgRoutineParams } from './pgsqlProceduralParams';
import { boundTablesFirst, resolveColumnRefToTable, type RelationResolver } from '../context/resolveColumnRefToTable';
import {
  applyInBufferTableOps,
  findInBufferAddedColumns,
  findInBufferCreatedTables,
  scanInBufferTableOps,
  type InBufferRename,
  type InBufferTable,
} from '../context/inBufferTables';
import { getPathHints } from '@/features/sql-resolver/json/pathHints';
import {
  getJsonPathHints,
  isJsonColumnDeclared,
  getLooseRelationships,
} from '@/features/sql-resolver/metaInfoHints/metaInfoHintProvider';
import { emitLooseRelJoinSnippets } from '@/features/intellisense/fkJoin/looseFkJoinSnippet';
import { applyJoinOnColumnRanking } from '@/features/intellisense/fkJoin/fkColumnRanking';
import { applyProjectionAwareGroupByRanking } from '@/features/intellisense/fkJoin/projectionAwareRanking';
import {
  collectArrayColumns,
  isAtArrayFunctionArg,
} from '@/features/intellisense/fkJoin/arrayFunctionArgAffinity';
import {
  applyJsonFunctionArgPromotion,
  collectBufferJsonFunctionColumns,
  isAtJsonFunctionFirstArg,
} from '@/features/intellisense/fkJoin/jsonFunctionArgAffinity';
import { isAtInsertTargetSlot } from '@/features/intellisense/context/isAtInsertTargetSlot';
import { classifyNextKeywordSlot, type NextKeywordSlotResult } from '../context/nextKeywordSlot';
import { getLegalNextKeywords, isRowLimitSlot } from '../context/legalNextKeywords';
import { isAtProceduralStatementStart } from '../context/proceduralStatementStart';
import { dollarQuotedBodyStart } from '../context/insideRoutineBody';
import {
  emitCompoundKeywordRemainders,
  keywordLabelsAlreadyOffered,
} from './compoundKeywordRemainder';
import { buildRowLimitOperandCandidates } from './rowLimitOperand';
import {
  classifyGraphSlot,
  collectGraphElementVariables,
} from '@/features/sql-grammar/document/editorContextSlot.graph';
import { computeClauseContext, tokenizeUntil } from '@/features/sql-grammar/document/editorContextClause';
import {
  PG_PGQ_FUNCTIONS,
  findGraphTableInfo,
  graphPatternContext,
  graphIdentEquals,
  graphStructuralKeyword,
  graphStructuralKeywordCandidate,
  type GraphTableInfo,
} from './pgqVocabulary';
import type { PropertyGraphMeta } from '@/core/engines/intellisenseTypes';

// PostgreSQL JSON functions whose first argument names the JSON document
// column — drives the JSON-arg affinity lift at `jsonb_path_query(p.▮` (parity
// with the oracle JSON_VALUE surface). Includes the PG17 SQL/JSON standard set.
const PGSQL_JSON_FN_NAMES: readonly string[] = [
  'jsonb_path_query',
  'jsonb_path_query_array',
  'jsonb_path_query_first',
  'jsonb_path_exists',
  'jsonb_extract_path',
  'jsonb_extract_path_text',
  'json_extract_path',
  'json_extract_path_text',
  'jsonb_array_elements',
  'jsonb_array_elements_text',
  'jsonb_each',
  'jsonb_each_text',
  'jsonb_object_keys',
  'jsonb_array_length',
  'jsonb_typeof',
  'JSON_VALUE',
  'JSON_QUERY',
  'JSON_EXISTS',
  'JSON_TABLE',
];
import {
  applyReferencesTargetPromotion,
  findFkSourceColumnStems,
} from '@/features/intellisense/fkJoin/referencesTargetInference';
import {
  findPgTriggerHostViaFunction,
  findTriggerHostTable,
  type TriggerHostTable,
} from '@/features/intellisense/resolver/triggerHostTable';
import type { LooseRelationship } from '@/types/electron';
import { extractJsonPathFromCursorPgsql } from '../operators/extractJsonPathFromCursor.pgsql';

const ENGINE: DatabaseEngineId = 'pgsql';
const PG_DEFAULT_SEARCH_PATH: readonly string[] = ['public'];
const PG_SYSTEM_SCHEMAS: ReadonlySet<string> = new Set([
  'pg_catalog',
  'information_schema',
  'pg_toast',
]);

// CB1.7 — position-aware keyword promotion. When the cursor sits in a
// recognised sub-position the listed continuation keywords promote to
// tier 1; other keywords stay at the default tier 7. Filtered against
// `PGSQL_KEYWORDS` so a renamed/removed keyword can't silently slip in.
const PGSQL_NEXT_AFTER_SELECT_PROJECTION: ReadonlySet<string> = new Set(
  ['FROM', 'UNION', 'EXCEPT', 'INTERSECT', 'FOR'].filter((k) =>
    PGSQL_KEYWORDS.has(k),
  ),
);

// PL/pgSQL body statement starters — promoted to tier 1 at a procedural
// statement-start (mirrors `ORACLE_PLSQL_STATEMENT_STARTERS`). Split in two:
// the SQL keywords the general list already carries (filtered against
// `PGSQL_KEYWORDS`) and the PL/pgSQL-only control words the general list does
// not know, which `emitPlpgsqlOnlyStatementStarters` adds directly.
const PLPGSQL_STATEMENT_STARTERS: readonly string[] = [
  'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'MERGE', 'WITH', 'BEGIN', 'DECLARE',
  'CASE', 'EXECUTE', 'CALL', 'COMMIT', 'ROLLBACK', 'NULL',
  'IF', 'LOOP', 'WHILE', 'FOR', 'FOREACH', 'EXIT', 'CONTINUE', 'RETURN',
  'PERFORM', 'RAISE', 'ASSERT', 'GET DIAGNOSTICS', 'OPEN', 'FETCH', 'CLOSE',
];
const PGSQL_PLPGSQL_STATEMENT_STARTERS: ReadonlySet<string> = new Set(
  PLPGSQL_STATEMENT_STARTERS.filter((k) => PGSQL_KEYWORDS.has(k)),
);
const PLPGSQL_ONLY_STATEMENT_STARTERS: readonly string[] =
  PLPGSQL_STATEMENT_STARTERS.filter((k) => !PGSQL_KEYWORDS.has(k));

const PGSQL_NEXT_AFTER_FROM_RELATION: ReadonlySet<string> = new Set(
  [
    'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS',
    'ON', 'USING', 'AS', 'ORDER', 'GROUP', 'HAVING', 'UNION', 'INTERSECT',
    'EXCEPT', 'LIMIT', 'OFFSET', 'FOR', 'WITH', 'RETURNING',
  ].filter((k) => PGSQL_KEYWORDS.has(k)),
);

// PG supports `ASC`/`DESC` direction plus `NULLS FIRST`/`NULLS LAST` null
// ordering at ORDER BY slots. ASC/DESC live in the curated single-token
// keyword list and surface naturally; NULLS FIRST/LAST are two-token
// keywords that the curated emitter cannot express, so we push them
// directly when the position is `order-by`.
const PGSQL_ORDER_BY_DIRECTION_KEYWORDS: readonly string[] = [
  'ASC',
  'DESC',
  'NULLS FIRST',
  'NULLS LAST',
];

/**
 * F5.3 Wave-4 — PG `EXTRACT(|<field> FROM <expr>)` first-arg slot.
 * PostgreSQL accepts a closed vocabulary of 22 date/time fields here.
 * Detector: cursor sits inside the open `(` of EXTRACT, before any token
 * that would close the field-name slot (a closing `)` or `FROM` keyword
 * past the cursor are both acceptable — we only check the prefix).
 */
const PGSQL_EXTRACT_FIRST_ARG_RE =
  /\bEXTRACT\s*\(\s*(?:[A-Za-z_][\w$]*)?\s*$/i;

const PGSQL_EXTRACT_FIELD_KEYWORDS: readonly string[] = [
  'YEAR',
  'MONTH',
  'DAY',
  'HOUR',
  'MINUTE',
  'SECOND',
  'DOW',
  'DOY',
  'EPOCH',
  'WEEK',
  'QUARTER',
  'MILLENNIUM',
  'CENTURY',
  'DECADE',
  'MICROSECONDS',
  'MILLISECONDS',
  'TIMEZONE',
  'TIMEZONE_HOUR',
  'TIMEZONE_MINUTE',
  'ISOYEAR',
  'ISODOW',
  'JULIAN',
];

/**
 * Position kinds where PL/pgSQL procedural locals are NOT valid completion
 * candidates. Skipping the entire `plpgsqlBlock` branch at these slots lets
 * the standard positionKind dispatch run, emitting tables at FROM, types
 * at DDL slots, and dotted-base members at dotted-access.
 */
function isPgsqlNonExpressionSlot(
  positionKind: CompletionPositionKind | undefined,
): boolean {
  if (!positionKind) return false;
  return (
    positionKind === 'from' ||
    // A DECLARE-section variable's own TYPE slot. A local is a NAME there, so
    // offering the block's other locals is always wrong — and it shadowed the
    // type list the caret actually wants (pgsql script.33 §3). The DECLARE
    // shape reaches the resolver as `'unknown'` (statement isolation cuts at
    // the previous declaration's `;`), so the CALLER additionally consults
    // `isPgsqlDataTypePosition` — this arm covers the seam-named carets.
    positionKind === 'data-type' ||
    positionKind === 'dotted-access' ||
    positionKind.startsWith('ddl-')
  );
}

function isPgsqlExtractFirstArgPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return PGSQL_EXTRACT_FIRST_ARG_RE.test(slice);
}

/**
 * F5.3 Wave-4 — PG window-frame slot detectors.
 *
 * `ROWS|RANGE|GROUPS BETWEEN <frame-start> AND <frame-end> [EXCLUDE <opts>]`
 *
 * - Frame-start: `UNBOUNDED PRECEDING`, `CURRENT ROW` (also `<n> PRECEDING`
 *   and `<n> FOLLOWING`, but those are integer-prefixed and not surfaceable
 *   as keyword completions).
 * - Frame-end: `UNBOUNDED FOLLOWING`, `CURRENT ROW`.
 * - EXCLUDE options: `CURRENT ROW`, `GROUP`, `TIES`, `NO OTHERS`.
 *
 * The grammar layer classifies these as `'unknown'`; the text-walk regex is
 * the gate.
 */
const PGSQL_FRAME_START_RE =
  /\b(?:ROWS|RANGE|GROUPS)\s+BETWEEN\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const PGSQL_FRAME_START_KEYWORDS: readonly string[] = [
  'UNBOUNDED PRECEDING',
  'CURRENT ROW',
];

const PGSQL_FRAME_END_RE =
  /\b(?:ROWS|RANGE|GROUPS)\s+BETWEEN\s+(?:UNBOUNDED\s+(?:PRECEDING|FOLLOWING)|\d+\s+(?:PRECEDING|FOLLOWING)|CURRENT\s+ROW)\s+AND\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const PGSQL_FRAME_END_KEYWORDS: readonly string[] = [
  'UNBOUNDED FOLLOWING',
  'CURRENT ROW',
];

const PGSQL_FRAME_EXCLUDE_RE =
  /\bEXCLUDE\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const PGSQL_FRAME_EXCLUDE_KEYWORDS: readonly string[] = [
  'CURRENT ROW',
  'GROUP',
  'TIES',
  'NO OTHERS',
];

function isPgsqlFrameStartPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return PGSQL_FRAME_START_RE.test(slice);
}

function isPgsqlFrameEndPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return PGSQL_FRAME_END_RE.test(slice);
}

function isPgsqlFrameExcludePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // EXCLUDE must follow an OVER (...) frame clause. Gate by the presence of
  // ROWS|RANGE|GROUPS BETWEEN ... AND ... preceding the EXCLUDE in the same
  // statement so we don't trip a bare `EXCLUDE` (e.g. CREATE … EXCLUDE …).
  if (!PGSQL_FRAME_EXCLUDE_RE.test(slice)) return false;
  return /\b(?:ROWS|RANGE|GROUPS)\s+BETWEEN\b/i.test(slice);
}

export const pgsqlResolver: OrchestratorResolver = {
  engineId: ENGINE,
  resolve: async (input) => resolvePgsql(input),
};

async function resolvePgsql(
  input: OrchestratorResolverInput,
): Promise<OrchestratorResolverResult> {
  const { model, position, monaco } = input;
  const text = model.getValue();
  const offset = model.getOffsetAt(position);

  // Script-mode `SET search_path TO <schema>` overrides the session search_path.
  // Forward scriptScope so ctx.scriptScope is populated; use scriptScope.searchPath
  // (when present) to promote in-scope schemas to tier 2 during candidate ranking.
  const scriptScope = input.scriptScope ?? {};
  const ctx = deriveCompletionContext(text, offset, ENGINE, scriptScope);
  const rankOptions = buildRankOptions(text, ctx, offset, input);
  // F5.3 Wave-4 → plan19 T7 — the fine-grained DDL slot kinds
  // (`ddl-index-column`, `ddl-index-method`, `ddl-fk-target`,
  // `ddl-on-conflict-target`, `ddl-trigger-event`, …) now arrive on
  // `ctx.positionKind` from the token slot seam (`classifyDdlSlot` via
  // `deriveCompletionContext`'s DDL-override gate). The string-walk
  // `classifyDdlPositionKind` override that used to run here is retired.

  // Inside a string/comment normally short-circuits. Exception: PL/pgSQL
  // dollar-quoted bodies (`$$...$$` / `$tag$...$tag$`) are functions
  // with their own DECLARE/BEGIN scope — we want completion to work
  // inside them. Detect that case and continue with a synthesized
  // PL/pgSQL context; otherwise bail.
  const plpgsqlBlock = findEnclosingPlpgsqlBlock(text, offset);

  const snap = input.connectionId
    ? getSchemaBusSnapshot(input.connectionId, input.database)
    : null;

  // Surface tables CREATEd earlier in the same buffer so subsequent
  // statements see them and their columns even before the schema-bus snapshot
  // refreshes. Augments the snapshot table list; the normal table-emit +
  // alias-resolution paths pick the new entries up transparently. Closes
  // goals/autocomplete-coverage pgsql script 42 (PARTITION-OF / INSERT target +
  // alias-bound columns of the just-CREATEd partitioned table).
  // Apply in-buffer RENAME/DROP to the snapshot FIRST, then merge in-buffer
  // CREATEs on top — so a `DROP TABLE foo; CREATE TABLE foo;` re-create leaves
  // `foo` present (applying ops after the merge would drop the just-created one).
  const augmentedTables = mergeInBufferTables(
    applyInBufferTableOps(snap?.tables ?? [], scanInBufferTableOps(text, offset, ENGINE), {
      keyOf: (t) => `${(t.schema ?? '').toLowerCase()}.${t.name.toLowerCase()}`,
      keyOfRef: (q, n) => `${(q ?? '').toLowerCase()}.${n.toLowerCase()}`,
      renameTable: (existing, op: InBufferRename) => ({
        name: op.toName,
        ...(op.toQualifier !== null ? { schema: op.toQualifier } : {}),
        kind: existing?.kind ?? 'table',
        columns: existing?.columns ?? [],
      }),
    }),
    findInBufferCreatedTables(text, offset, ENGINE),
    findInBufferAddedColumns(text, offset, ENGINE),
  );
  // The session's search_path — a script `SET search_path` wins over the
  // snapshot's. Exact unless it is PostgreSQL's default standing in for a path
  // that could not be read (`PgSearchPath`).
  const pgPath: PgSearchPath = {
    schemas: scriptScope.searchPath ?? snap?.searchPath ?? PG_DEFAULT_SEARCH_PATH,
    exact: scriptScope.searchPath !== undefined || snap?.searchPath !== undefined,
  };
  // The table a relation reference names, as PostgreSQL resolves it — the
  // MetaInfo enum / JSON-path collectors read the hints of THAT table.
  const resolveTable = (name: string, schema: string | undefined): SnapshotTable | undefined =>
    resolvePgRelation(augmentedTables, foldIdentifier(name), schema ? foldIdentifier(schema) : null, pgPath);

  // JCS Slice C4 — JSON path completion fires even inside a single-quoted
  // string when the string is in a recognised JSON-path position (right
  // after `->`, `->>`, `JSON_VALUE(col, `, etc.). Path hints come from the
  // per-session sample cache populated by the "Detect JSON paths…" dialog;
  // when the cache is empty, the emitter returns 0 candidates and we fall
  // through to the normal string-bail behavior.
  if (!plpgsqlBlock && input.connectionId && input.database) {
    const pathHintsCandidates = collectPgJsonPathCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: input.database,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (pathHintsCandidates.length > 0) {
      const wordInfo = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };
      const ranked = rankCandidates(pathHintsCandidates, ctx, String(wordInfo.word ?? ''), rankOptions);
      return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, range)) };
    }
    // JSONB key-existence operator: `(col -> 'items') ? '|'`. Offers the bare
    // child keys available under the navigated sub-path so the user can test
    // for one — a slot the function/arrow JSON-path detector doesn't cover.
    const keyExistenceCandidates = collectPgJsonbKeyExistenceCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: input.database,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (keyExistenceCandidates.length > 0) {
      const wordInfo = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };
      const ranked = rankCandidates(keyExistenceCandidates, ctx, String(wordInfo.word ?? ''), rankOptions);
      return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, range)) };
    }
    // JSONB containment operator: `col @> '{"|":…}'`. Inside the contained
    // object literal, offer the column's top-level keys — a slot neither the
    // arrow/function path detector nor the `?` collector covers.
    const containmentCandidates = collectPgJsonbContainmentCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: input.database,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (containmentCandidates.length > 0) {
      const wordInfo = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };
      const ranked = rankCandidates(containmentCandidates, ctx, String(wordInfo.word ?? ''), rankOptions);
      return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, range)) };
    }
  }

  // Phase 3 — enum-value completion. Fires (like JSON-path) BEFORE the
  // string-bail because the cursor sits inside an unclosed string literal at
  // `WHERE status = '|`. When the comparison LHS resolves to a column whose
  // distinct values are cached in MetaInfo, surface them as string-literal
  // candidates. Empty cache → 0 candidates → fall through to normal behavior.
  // Suppressed inside a PL/pgSQL dollar-quoted body (matching the JSON
  // collectors above) — those bodies route through the dedicated plpgsqlBlock
  // branch and must not leak enum-value completion.
  if (!plpgsqlBlock && input.connectionId && input.database) {
    const enumCandidates = collectPgEnumValueCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: input.database,
      snapshotTables: augmentedTables,
      aliasBindings: ctx.aliasBindings,
      resolveTable,
    });
    if (enumCandidates.length > 0) {
      const edit = computeEnumValueEdit(model, position, text, offset);
      const ranked = rankCandidates(enumCandidates, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)),
      };
    }
  }

  // `DATE_TRUNC('▮', ts)` / `DATE_PART('▮', ts)` — a closed field vocabulary
  // inside a string literal, so it fires BEFORE the string bail exactly like
  // the JSON-path and enum-value collectors above. Needs no connection: the
  // vocabulary is the dialect's, not the database's. Closes pgsql script.60 §2.
  {
    const dateFieldFn = findPgDateFieldLiteralSlot(text, ctx.statementRange.start, offset);
    if (dateFieldFn) {
      const fieldCandidates: CompletionCandidate[] = getPgDateFields(dateFieldFn).map(
        (field, index) => ({
          // Bare label: the caret is INSIDE the quotes, which the buffer
          // already carries.
          label: field,
          insertText: field,
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          detail: `${dateFieldFn} field`,
          orderHint: index,
        }),
      );
      const edit = computeEnumValueEdit(model, position, text, offset);
      const ranked = rankCandidates(fieldCandidates, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)),
      };
    }
  }

  // `nextval('▮')` / `currval('▮')` / `setval('▮', …)` — PG names a sequence
  // with a string LITERAL, so this slot fires before the string bail exactly
  // like the enum-value and DATE_TRUNC field collectors above. The replace
  // range covers only the in-progress name after the (already typed)
  // `schema.` qualifier, and the inserted text is bare — the quotes are
  // already in the buffer. Closes pgsql script.45 §5/§7/§8.
  {
    const seqSlot = findPgSequenceLiteralSlot(text, ctx.statementRange.start, offset);
    if (seqSlot) {
      const seqCandidates = emitPgSequenceCandidates(
        seqSlot,
        snap?.sequences ?? [],
        text,
        scriptScope.searchPath ?? snap?.searchPath ?? PG_DEFAULT_SEARCH_PATH,
      );
      if (seqCandidates.length > 0) {
        const seqRange = {
          startLineNumber: position.lineNumber,
          startColumn: position.column - (offset - seqSlot.partialStart),
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        };
        const ranked = rankCandidates(seqCandidates, ctx, seqSlot.partial, rankOptions);
        return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, seqRange)) };
      }
    }
  }

  const wordInfo = model.getWordUntilPosition(position);
  const typed = String(wordInfo.word ?? '');

  // The `&& !plpgsqlBlock` exception this used to carry was a workaround for
  // `deriveContext` flagging an entire dollar-quoted routine body as "in
  // string" — without it there was no completion in function bodies at all.
  // The body is now analyzed as SQL and the flag means what it says, so a
  // genuine literal or comment INSIDE a body suppresses like anywhere else.
  if (ctx.insideStringOrComment) return { suggestions: [] };
  // Cold cache → warm fire-and-forget. Subsequent keystrokes will see the
  // populated snapshot — same fire-and-forget contract MSSQL uses.
  if (!snap && input.connectionId && input.database) {
    void ensurePgSchema({
      connectionId: input.connectionId,
      database: input.database,
    }).catch(() => undefined);
  }

  const onConflictTarget = findInsertOnConflictTarget(
    text,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );
  // F5.2-CB2 — When the cursor is inside a CREATE TRIGGER body OR inside a
  // PL/pgSQL trigger function (linked via CREATE TRIGGER … EXECUTE FUNCTION),
  // NEW.|/OLD.| binds to the trigger target-table's columns. We try the
  // function-body link first because it's the common PG idiom; the direct
  // form (`CREATE TRIGGER … ON <table>` with an inline body) is rare.
  const triggerHostTable =
    findPgTriggerHostViaFunction(text, offset) ??
    findTriggerHostTable(text, offset, 'pgsql');

  const candidates = await buildCandidates({
    ctx,
    text,
    connectionId: input.connectionId,
    database: input.database,
    snapshotTables: augmentedTables,
    snapshotSchemas: snap?.schemas ?? [],
    snapshotRoutines: snap?.routines ?? [],
    snapshotForeignKeys: snap?.foreignKeys ?? [],
    looseRels:
      input.connectionId && input.database
        ? getLooseRelationships(input.connectionId, input.database)
        : [],
    snapshotExtensions: snap?.extensions ?? [],
    snapshotUserTypes: snap?.userTypes ?? [],
    // Script-mode search_path override (scriptScope.searchPath) takes precedence over
    // the snapshot's stored search_path and the global default.
    searchPath: pgPath.schemas,
    searchPathExact: pgPath.exact,
    cteBindings: ctx.cteBindings,
    onConflictTarget,
    triggerHostTable,
    plpgsqlBlock,
    propertyGraphs: snap?.propertyGraphs ?? [],
  });

  // `USING (▮)` / `ROLLUP(▮)` / Oracle's `SAMPLE (▮)` admit operands or
  // nothing — never the tables and FK-join snippets the full emission adds.
  const scoped = applyIndexTargetFilter(
    applyParenScopeFilter(candidates, text, offset, ctx.statementRange.start, ENGINE),
    text, offset, ctx.statementRange.start, ENGINE,
  );
  // GROUP BY scope (`context/groupByScope.ts`) — once the statement groups,
  // a bare non-grouped column is ERROR 42803 at HAVING / ORDER BY, so the
  // candidate is dropped rather than demoted. Self-gated: HAVING / ORDER BY /
  // post-GROUP-BY select list only, and only when the GROUP BY clause parsed
  // with confidence. Wired at the one place every arm's candidates converge,
  // so the clause arm, the predicate-head arm and the value-slot refusals are
  // covered by one call. Closes pgsql script.58 §2.
  const grouped = applyGroupByScopeFilter(scoped, {
    text,
    offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engineId: ENGINE,
    positionKind: ctx.positionKind,
  });
  const withDocs = enrichCandidatesWithDocs(grouped, {
    aliasBindings: ctx.aliasBindings,
    snapshotTables: augmentedTables,
    foreignKeys: snap?.foreignKeys ?? [],
    fold: foldIdentifier,
  });
  const ranked = rankCandidates(withDocs, ctx, typed, rankOptions);

  const range = {
    startLineNumber: position.lineNumber,
    startColumn: wordInfo.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: wordInfo.endColumn,
  };
  const suggestions = ranked.map((item) => toMonacoItem(item, monaco, range));
  return { suggestions };
}

// Merge in-buffer CREATE TABLE entries with the schema-bus snapshot. Skips
// in-buffer entries whose folded `<schema>.<name>` collides with a snapshot
// entry — the snapshot already reflects the table. The generic `qualifier`
// segment maps to PG's `schema`.
function mergeInBufferTables(
  snapshot: ReadonlyArray<SnapshotTable>,
  inBuffer: ReadonlyArray<InBufferTable>,
  addedColumns: ReadonlyMap<
    string,
    ReadonlyArray<{ readonly name: string; readonly dataType: string }>
  > = new Map(),
): ReadonlyArray<SnapshotTable> {
  if (inBuffer.length === 0 && addedColumns.size === 0) return snapshot;
  const keyOf = (schema: string | null | undefined, name: string): string =>
    `${(schema ?? '').toLowerCase()}.${name.toLowerCase()}`;
  const known = new Set<string>();
  for (const t of snapshot) known.add(keyOf(t.schema, t.name));
  const merged: SnapshotTable[] = [...snapshot];
  for (const t of inBuffer) {
    const schema = t.qualifier ?? null;
    if (known.has(keyOf(schema, t.name))) continue;
    merged.push({
      name: t.name,
      schema: schema ?? undefined,
      kind: t.isTemporary ? 'temporary-table' : 'table',
      columns: t.columns.map((c) => ({ name: c.name, dataType: c.dataType })),
    });
  }
  if (addedColumns.size === 0) return merged;
  // `ALTER TABLE … ADD COLUMN` earlier in the buffer. The snapshot only knows
  // the columns the table had when the connection warmed, so a later caret in
  // the same script (`FOREIGN KEY (film_id)` after `ADD COLUMN film_id`) saw
  // none of them. Appended, never replacing: the snapshot's own columns stay
  // first and an ADD that duplicates one is ignored.
  return merged.map((t) => {
    // An unqualified `ALTER TABLE film` names the search_path relation, so a
    // qualifier-less key has to match on the bare name alone.
    const added =
      addedColumns.get(`${(t.schema ?? '').toLowerCase()}|${t.name.toLowerCase()}`)
      ?? addedColumns.get(`|${t.name.toLowerCase()}`);
    if (!added || added.length === 0) return t;
    const have = new Set(t.columns.map((c) => c.name.toLowerCase()));
    const extra = added.filter((c) => !have.has(c.name.toLowerCase()));
    if (extra.length === 0) return t;
    return { ...t, columns: [...t.columns, ...extra] };
  });
}

type SnapshotTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  readonly columns: ReadonlyArray<{
    readonly name: string;
    readonly dataType: string;
    // Carried by the bus snapshot's ColumnMeta; read by the pkFirst mode of
    // the REFERENCES / constraint column-list emission.
    readonly isPrimaryKey?: boolean;
    // Single-column UNIQUE coverage (bus ColumnMeta.isUnique) — read by the
    // `ON CONFLICT (▮)` conflict-target filter, where only a column a unique
    // index can arbitrate on is legal.
    readonly isUnique?: boolean;
    readonly jsonClassification?: import('@/features/sql-resolver/types/schema').JsonClassification;
    // Native `pg_enum` members (introspection-resolved) — drives native
    // enum-value completion when the column isn't a declared MetaInfo enum.
    readonly enumValues?: readonly string[];
    // Carried by the bus snapshot's ColumnMeta (T18b, D13) — read to build
    // the NULL keyword legality of a typed INSERT VALUES element.
    readonly nullable?: boolean;
    // Engine-assigned value (`GENERATED … AS IDENTITY`, or the `SERIAL`
    // spelling of it) / generated expression — read by the INSERT column-list
    // demotion (`insertableColumnDemotion.ts`). `ColumnMeta.hasDefault` is
    // deliberately NOT read: an ordinary DEFAULT makes a column OPTIONAL in an
    // INSERT, not engine-owned, and demoting it would bury a column writers
    // list all the time. Undefined on snapshots that predate the flags.
    readonly isIdentity?: boolean;
    readonly isComputed?: boolean;
  }>;
  // Indexes on the relation — the index-NAME vocabulary of `DROP INDEX ▮`
  // (see `IndexMeta`; undefined on engines whose adapter does not source it).
  readonly indexes?: ReadonlyArray<{
    readonly name: string;
    readonly columns: readonly string[];
    readonly isUnique: boolean;
    readonly isPrimary: boolean;
  }>;
};

/** Structural subset of the bus snapshot's `UserTypeMeta` the resolver reads. */
type SnapshotUserType = {
  readonly name: string;
  readonly schema?: string;
  /** `pg_type.typtype`, projected — enums, domains, range types and
   *  standalone composites are ALL legal at a data-type slot, and the kind is
   *  what the completion's `detail` tells the reader apart. Optional: fixtures
   *  that predate the `pg_type` scan carry only the name. */
  readonly kind?: string;
};

type SnapshotRoutine = {
  readonly name: string;
  readonly schema?: string;
  readonly kind: string;
  /** Return type (bus `RoutineMeta.returns`) — drives value-slot routine
   *  tiering (T8); absent on engines/objects without one. */
  readonly returns?: string;
};

type BuildArgs = {
  readonly ctx: ReturnType<typeof deriveCompletionContext>;
  readonly text: string;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly snapshotSchemas: readonly string[];
  readonly snapshotRoutines: readonly SnapshotRoutine[];
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly looseRels: readonly LooseRelationship[];
  readonly snapshotExtensions: readonly string[];
  /** User-defined types surfaced at data-type slots (`mpaa_rating`, and the
   *  domains / composites a `public.▮` type qualifier drills into). The SCHEMA
   *  is carried, not dropped: a schema-qualified type slot must emit only the
   *  types that actually live in the named schema (script.39 §5/§6). */
  readonly snapshotUserTypes: readonly SnapshotUserType[];
  readonly searchPath: readonly string[];
  /** `searchPath` is the session's own (read from the server or SET in the script) — `PgSearchPath.exact`. */
  readonly searchPathExact: boolean;
  readonly cteBindings: readonly CteBinding[];
  readonly onConflictTarget: PgsqlInsertTarget | null;
  readonly triggerHostTable: TriggerHostTable | null;
  readonly plpgsqlBlock: {
    readonly variables: readonly PlpgsqlVariable[];
    readonly bodyStart: number;
    readonly bodyEnd: number;
  } | null;
  /** T18b — typed VALUES element enum-value lookup (MetaInfo is keyed per
   *  connection/database). Absent in some tests; the emitter degrades to no
   *  enum-value candidates when either is missing. */
  readonly connectionId?: string;
  readonly database?: string;
  /** SQL/PGQ property graphs (`GRAPH_TABLE( ▮` targets) with labels/properties.
   *  Empty when the server has none / is pre-PG19 (Task 23 only populates the
   *  field on PG19+), so the graph gate stays inert and completion is
   *  byte-identical to the non-graph path. PG19 ships no PGQ scalar functions,
   *  so nothing here is version-gated in-line; `GRAPH_TABLE` itself gates via
   *  the orchestrator's version-gating post-process (catalog `addedIn: '19'`). */
  readonly propertyGraphs: readonly PropertyGraphMeta[];
};

async function buildCandidates(args: BuildArgs): Promise<CompletionCandidate[]> {
  const { ctx, snapshotTables, snapshotSchemas, snapshotForeignKeys, searchPath } = args;
  const out: CompletionCandidate[] = [];

  // Compound-keyword remainder — when the caret is attached to the second (or
  // third) word of a multi-word keyword (`CURRENT R▮`, `UNBOUNDED P▮`,
  // `NEXT V▮`, `REFRESH ON D▮`, `READS S▮`, `ON DUPLICATE K▮`), offer what is
  // left of that keyword. Runs FIRST so the keyword-only short-circuits below
  // (which return `out` as it stands) carry it; additive, so a caret whose head
  // word is really an identifier keeps everything else it had. See
  // `compoundKeywordRemainder.ts`.
  emitCompoundKeywordRemainders(
    out,
    ENGINE,
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.positionKind,
  );

  // P2 / Task 24 — SQL/PGQ GRAPH_TABLE completion. A text-walk gate (mirroring
  // the Oracle Task-20 dispatch, `collectOracleGraphCandidates`) rather than a
  // `positionKind` branch: inside a canonical `FROM GRAPH_TABLE( … )` the
  // clause machine names the slot `'from'` (and `elem.▮` short-circuits to
  // `'dotted-access'` before the slot seam runs), so a CPK-only dispatch would
  // only catch the degenerate no-FROM form. Running the SAME Task-18
  // `classifyGraphSlot` here catches every form. Returns null (falls through)
  // when there are no property graphs or the cursor is not inside a
  // `GRAPH_TABLE (`, so non-PGQ completion is byte-identical.
  const graphCandidates = collectPgGraphCandidates(args);
  if (graphCandidates) return graphCandidates;

  // PL/pgSQL block — DECLARE-block variables become tier-1 candidates.
  // The ANTLR-backed emitter (`emitPgsqlProceduralLocalCandidates`) is the
  // source of truth for variables; it walks the same `variables` array via
  // `resolveScopePlPgSql` and degrades gracefully (empty result) when the
  // grammar bundle is not yet loaded.
  // The legacy lightweight `emitPlpgsqlVariables` is intentionally NOT called
  // here — calling both would push each variable twice into `out` (once with
  // `(local variable)` detail, once with `(PL/pgSQL var)` detail).
  //
  // This used to emit the locals plus a keyword list and RETURN, so a body's
  // SELECT/WHERE/ORDER BY positions saw no columns, tables or functions at
  // all. That was a consequence of `deriveContext` reading the whole
  // dollar-quoted body as a string literal — every position inside it came
  // back `'unknown'`, so there was no position to dispatch on. The body is now
  // analyzed as the SQL it is (`findEnclosingDollarQuotedBody`), so the locals
  // are merged into the normal dispatch instead of replacing it — the same
  // shape the oracle / mysql / mssql resolvers have always had.
  if (
    args.plpgsqlBlock &&
    !isPgsqlNonExpressionSlot(ctx.positionKind) &&
    // The DECLARE-section type slot arrives as `'unknown'` — see the note in
    // `isPgsqlNonExpressionSlot`.
    !isPgsqlDataTypePosition(args.text, ctx.offset, ctx.statementRange.start)
  ) {
    // F5.2-CB2 — NEW.|/OLD.| inside a trigger function body resolves to
    // the trigger target-table's columns. `deriveContext` flags the entire
    // dollar-quoted body as "in string" and so doesn't compute a dotted
    // base; we extract one ourselves via the same `extractDottedBase`
    // helper used by the orchestrator. Surface ONLY the host columns at
    // a NEW./OLD. position, not the procedural locals.
    const procDotted = extractDottedBase(args.text, ctx.offset, args.plpgsqlBlock.bodyStart);
    if (procDotted && procDotted.parts.length === 1) {
      const baseFolded = foldIdentifier(procDotted.parts[0]!);
      if ((baseFolded === 'new' || baseFolded === 'old') && args.triggerHostTable) {
        emitPgsqlTriggerHostColumns(out, args.triggerHostTable, snapshotTables, baseFolded);
        return out;
      }
    }
    const beforeLocals = out.length;
    await emitPgsqlProceduralLocalCandidates(args.text, ctx.offset, out);
    // The ANTLR path degrades when the body does not parse — the normal state
    // while it is being written — and it degrades PARTIALLY (parameters can
    // survive while the DECLARE variables do not). The block scan already
    // extracted that section by pure text, so it always runs and fills the gaps
    // by name rather than only when the grammar produced nothing at all.
    {
      const emitted = new Set(out.slice(beforeLocals).map((c) => c.label.toLowerCase()));
      const addFallback = (name: string, detail: string) => {
        if (emitted.has(name.toLowerCase())) return;
        emitted.add(name.toLowerCase());
        out.push({
          label: name,
          insertText: name,
          isSnippet: false,
          kind: 'variable',
          tier: 1,
          detail,
          filterText: name,
        });
      };
      for (const v of args.plpgsqlBlock.variables) {
        addFallback(v.name, `${v.dataType ?? ''} (PL/pgSQL var)`.trim());
      }
      for (const param of extractPgRoutineParams(args.text, args.plpgsqlBlock.bodyStart)) {
        addFallback(param.name, 'parameter');
      }
    }
    // Deliberately no `return` — the position dispatch below adds the columns,
    // relations and functions that are legal alongside the locals.
  }

  const positionKind = ctx.positionKind;

  if (positionKind === 'dotted-access' && ctx.dottedBase) {
    emitDottedAccess(out, args);
    return out;
  }

  // `ROLLBACK TO ▮` / `RELEASE ▮` — a savepoint NAME slot. The name is
  // transaction state the buffer itself declared, so no schema emission can
  // reach it; before this the caret fell to `unknown` and answered with the
  // table list. Shared across all five engines (each spells the statements
  // differently — see `savepointSlot.ts`). Closes sqlite script.50 §9/§10.
  if (emitSavepointNameCandidates(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // Index-NAME slot. `DROP INDEX ▮` names an INDEX, not a relation, but
  // the caret falls to 'unknown' and answered with the table list. Shared
  // recognizer + emitter (`indexNameSlot.ts`); it claims the caret only when
  // the snapshot actually carries index metadata for the engine, so an engine
  // whose adapter has not populated `TableMeta.indexes` yet is unchanged.
  if (emitIndexNameCandidates(out, {
    text: args.text,
    offset: ctx.offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engine: ENGINE,
    tables: args.snapshotTables,
    format: formatIdentifier,
  })) {
    return out;
  }

  // `ALTER TABLE t RENAME TO ▮` — the NEW name. Every name the schema could
  // offer is a name the rename would collide with, so the slot is closed.
  // Shared with the other RENAME-capable engines (see `renameTargetSlot.ts`).
  if (isAtRenameTargetSlot(args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // `CREATE TYPE … AS RANGE (subtype_diff = ▮` — a DDL option value, not a
  // comparison. The `=` makes the value-slot recognizer claim the caret as
  // `comparison-rhs`, so this has to run BEFORE the scalar-value branch below
  // or the shared policy answers with `ALL` / `ANY` / `(SELECT …)`. Closes
  // pgsql script.40 §3.
  {
    const rangeOption = findPgRangeOptionValueSlot(
      args.text, ctx.statementRange.start, ctx.offset,
    );
    if (rangeOption === 'subtype') {
      // `subtype = ▮` names the range's ELEMENT TYPE, so the answer is the
      // same type vocabulary every other type slot gets — it just cannot get
      // there on its own, because the `=` hands the caret to the
      // comparison-RHS policy before `isPgsqlDataTypePosition` is consulted.
      // Closes pgsql script.40 §1/§2.
      emitPgsqlDataTypeCandidates(out, args.snapshotUserTypes, {
        text: args.text,
        cursorOffset: ctx.offset,
      });
      return out;
    }
    if (rangeOption) {
      const fns =
        rangeOption === 'subtype_diff'
          ? PG_RANGE_SUBTYPE_DIFF_FUNCTIONS
          : PG_RANGE_CANONICAL_FUNCTIONS;
      fns.forEach((name, index) => {
        out.push({
          label: name,
          insertText: name,
          isSnippet: false,
          kind: 'routine',
          tier: 1,
          detail: `pg_catalog range ${rangeOption} function`,
          orderHint: index,
        });
      });
      return out;
    }
  }

  // Value-slot plan T8 — scalar-value branch. Reached ONLY when the engine's
  // capability table (or the T5b test setter) admits the slot's role; dormant
  // in production until the T13a flip. The shared policy owns the candidate
  // set; pg_catalog functions stay engine-emitted at their tiers.
  if (positionKind === 'scalar-value' && ctx.valueSlot) {
    const { adapter, scope } = buildPgsqlValueSlotAdapter({
      slot: ctx.valueSlot,
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      searchPath,
      searchPathExact: args.searchPathExact,
      // C2 P1-1 — virtual system-catalog fallback: same key derivation as
      // `emitAliasColumns`'s F5.3 Wave-4 arm over PG_SYSTEM_TABLE_COLUMNS.
      virtualColumnsFor: (binding) => {
        const relationFolded = foldIdentifier(binding.relation);
        const schemaFolded = binding.schema ? foldIdentifier(binding.schema) : null;
        const sysKey = schemaFolded ? `${schemaFolded}.${relationFolded}` : null;
        return sysKey
          ? PG_SYSTEM_TABLE_COLUMNS[sysKey]?.map((name) => ({ name }))
          : undefined;
      },
    });
    await emitValueSlotCandidates(out, ctx.valueSlot, adapter, scope);
    emitPgCatalogFunctions(out, args.snapshotRoutines);
    // T13a no-worse parity (F5.3-R) — the clause path surfaced SELECT-list
    // projection aliases at HAVING positions (script.16 §3 pins
    // `film_count`/`avg_rate` at `HAVING COUNT(*) > ▮`). A comparison-RHS
    // slot that overrode the HAVING clause must keep them. Recompute the
    // clause with the same machine `deriveCompletionContext` used (the slot
    // path drops it); WHERE/JOIN-ON slots never gain aliases — projections
    // aren't computed yet at those points in SQL evaluation order.
    const scalarClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, 'postgres',
    )?.clause;
    if (scalarClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, 'pgsql', 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // Predicate-head plan P5/P7 — predicate-head branch, LIVE in production
  // since the P7 flip (pgsql carries all three head roles in
  // `predicateHeadCapabilities.ts`). Reached when the capability gate admits
  // the head's role. Shape-mirrors the scalar-value branch above: shared
  // policy owns the candidate set, pg_catalog builtins stay engine-emitted at
  // their existing tier (the PH-D4 builtin exemption — they carry no return
  // metadata to tier against, exactly as the scalar branch ships them).
  if (positionKind === 'predicate-head' && ctx.predicateHead) {
    const { adapter } = buildPgsqlValueSlotAdapter({
      // No `slot`: PH-D3's no-LHS mode. The overload makes this a distinct
      // signature, so a scalar-value caller cannot reach it by dropping its
      // slot.
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotRoutines: args.snapshotRoutines,
      snapshotForeignKeys,
      searchPath,
      searchPathExact: args.searchPathExact,
      virtualColumnsFor: (binding) => {
        const relationFolded = foldIdentifier(binding.relation);
        const schemaFolded = binding.schema ? foldIdentifier(binding.schema) : null;
        const sysKey = schemaFolded ? `${schemaFolded}.${relationFolded}` : null;
        return sysKey
          ? PG_SYSTEM_TABLE_COLUMNS[sysKey]?.map((name) => ({ name }))
          : undefined;
      },
    });
    await emitPredicateHeadCandidates(out, { head: ctx.predicateHead }, adapter);
    emitPgCatalogFunctions(out, args.snapshotRoutines);
    // DDL row scope — `CREATE INDEX … ON t (…) WHERE ▮` (a partial index).
    // The statement has no FROM clause, so `ctx.aliasBindings` is empty and
    // the head policy above had no relation to scope columns to; the INDEXED
    // table is the predicate's row scope and its columns are the only legal
    // column references here. Additive, so the head's own snippets
    // (`EXISTS (SELECT …)`, `NOT`) survive. Closes pgsql script.31 §5.
    //
    // ENGINE SCOPE: sqlite (partial index) and mssql (filtered index) carry
    // the same construct and inherit the same gap, but their spec rows —
    // sqlite script.30 §4/§5, mssql script.28 §7/§8 — currently DOCUMENT the
    // absence ("no scoped columns here … a pre-existing DDL alias-binding
    // gap"), so closing it there is a change to those specs' claims and
    // belongs with whoever owns them. mysql and oracle have no conditional
    // index at all.
    if (isPgsqlIndexPredicate(args.text, ctx.statementRange.start, ctx.offset)) {
      const indexTarget = findPgIndexTargetTable(
        args.text, ctx.statementRange.start, ctx.offset,
      );
      if (indexTarget) {
        emitPgTargetTableColumns(out, indexTarget, snapshotTables, pgPathOf(args));
      }
    }
    // Post-policy parity (P1-sol F8) — the same engine-specific follow-up the
    // scalar-value branch replays: the clause path surfaces SELECT-list
    // projection aliases at HAVING positions, and a head slot that overrode
    // the HAVING clause must keep them. WHERE/JOIN-ON heads never gain
    // aliases — projections aren't computed yet at those points in SQL
    // evaluation order.
    const headClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, 'postgres',
    )?.clause;
    if (headClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, 'pgsql', 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // §4.5 — named-window completion after `OVER `/`OVER (`. Additive (tier 1,
  // no short-circuit): the WINDOW-clause names rank above the position's
  // generic keyword/column vocabulary, which the pipeline still emits below.
  // Reads `QueryStructure.windows` for the current statement (decision 7).
  if (!ctx.insideStringOrComment) {
    emitNamedWindowCandidates({
      out,
      text: args.text,
      offset: ctx.offset,
      statementStart: ctx.statementRange.start,
      statementEnd: ctx.statementRange.end,
      engine: 'postgres',
    });
  }

  // F5.3 Wave-4 — PG `EXTRACT(|<field> FROM <expr>)` first-arg slot. The
  // 22 date/time fields form a closed vocabulary; this dispatch
  // short-circuits to that set and suppresses the general completion
  // candidates. Closes goals/autocomplete-coverage pgsql script.60 §1.
  if (isPgsqlExtractFirstArgPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of PGSQL_EXTRACT_FIELD_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — PG window-frame slot dispatch. Each of the three frame
  // sub-slots (frame-start, frame-end, EXCLUDE-options) gets its own narrow
  // keyword set. The grammar layer classifies all three as `'unknown'`;
  // text-walk regexes are the gate. Closes goals/autocomplete-coverage
  // pgsql script.52 §3, §4, §5.
  if (isPgsqlFrameStartPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of PGSQL_FRAME_START_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }
  if (isPgsqlFrameEndPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of PGSQL_FRAME_END_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }
  if (isPgsqlFrameExcludePosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of PGSQL_FRAME_EXCLUDE_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // F5.3 Wave-4 — PG data-type slot (CREATE TABLE column-type, ALTER TABLE
  // ADD COLUMN type, CREATE FUNCTION param type / RETURNS type, CAST(...AS),
  // `::`-cast suffix). The grammar's clause classifier maps these slots to
  // `'unknown'`; the narrow text-walk in `isPgsqlDataTypePosition`
  // recognises them by surface keywords. When matched, surface the curated
  // PG type list — preceded by the database's user-defined enum types
  // (`mpaa_rating`, projected onto `snapshotUserTypes` from `pg_enum`) — as
  // the dominant suggestion set and short-circuit. Mirrors the mssql
  // data-type emitter contract. Closes F5.3 gaps script.29 §3, script.13 §3,
  // and script.30 §3 (user-defined enum types in the type slot).
  // The slot seam also names this position (`ALTER TABLE t ADD col ▮`,
  // `CAST(x AS ▮`, a CREATE TABLE column definition) — honour it as well as the
  // surface text-walk, which does not cover every shape.
  if (
    positionKind === 'data-type' ||
    isPgsqlDataTypePosition(args.text, ctx.offset, ctx.statementRange.start)
  ) {
    // A function's `RETURNS ▮` additionally accepts the two pseudo-types
    // (`trigger`, `event_trigger`) that are illegal at every other type slot,
    // and they lead: a caret here is far more often a trigger function than a
    // scalar one. Closes pgsql script.35 §1.
    const returnsSlot = isPgsqlReturnsTypeSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
    );
    if (returnsSlot) {
      // `orderHint` is what keeps them AHEAD of the scalar list — within a
      // tier the ranker falls back to alphabetical, which buried `trigger`
      // behind every built-in starting with a letter before `t`.
      PGSQL_RETURNS_ONLY_TYPES.forEach((pseudo, index) => {
        out.push({
          label: pseudo,
          insertText: pseudo,
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          orderHint: index,
          detail: 'PG pseudo-type (RETURNS only)',
        });
      });
    }
    emitPgsqlDataTypeCandidates(out, args.snapshotUserTypes, {
      text: args.text,
      cursorOffset: ctx.offset,
    });
    return out;
  }

  // DDL unknown-position sweep — the four slots the seam already NAMED for
  // pgsql but the resolver had no arm for, so the caret fell through to the
  // relation-emitting fallback and flooded with tables.
  if (positionKind === 'ddl-on-conflict-target') {
    // `INSERT … ON CONFLICT (▮` — only a column a unique index can arbitrate
    // on is legal here; anything else is a runtime error ("ON CONFLICT clause
    // does not match any PRIMARY KEY or UNIQUE constraint"), so the rest are
    // FILTERED, not deprioritised. Mirrors the sqlite arm. Closes pgsql
    // script.8 §3.
    if (args.onConflictTarget) {
      emitOnConflictTargetColumns(out, args.onConflictTarget, snapshotTables, pgPathOf(args), {
        uniqueOnly: true,
      });
    }
    return out;
  }
  if (positionKind === 'ddl-create-table-expression') {
    // A CHECK / PK / UNIQUE / FK / GENERATED-AS column list, or a
    // `PARTITION BY (▮` expression, inside a CREATE TABLE. The table does not
    // exist in the snapshot yet, so the answer is the columns declared SO FAR
    // in the statement's own body. Closes pgsql script.29 §6 + script.42 §3.
    emitInProgressCreateTableColumns(
      out,
      extractInProgressCreateTableColumns(args.text, ctx.statementRange.start, ctx.offset),
    );
    return out;
  }
  if (positionKind === 'ddl-target-table-column') {
    // `CREATE TRIGGER … UPDATE OF ▮ … ON <tbl>` — the restricted-column list.
    // Its owning table is named AFTER the caret, so bind it by looking ahead.
    const trigger = findTriggerOnTableAhead(args.text, ctx.offset, ctx.statementRange.end);
    if (trigger) {
      emitPgTargetTableColumns(
        out,
        {
          // The written tokens: a delimited `"Orders"` keeps its case.
          schema: trigger.schemaToken ? foldIdentifier(trigger.schemaToken) : null,
          name: foldIdentifier(trigger.nameToken),
        },
        snapshotTables,
        pgPathOf(args),
      );
    }
    return out;
  }

  // F5.3 Wave-4 — DDL slot dispatch (mirrors sqliteResolver). Each branch is
  // purposely narrow: the slot's candidate set is a strict subset of the
  // general completion universe so unrelated suggestions don't dilute the
  // user's choices.
  if (positionKind === 'ddl-index-column') {
    const target = findPgIndexTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) emitPgTargetTableColumns(out, target, snapshotTables, pgPathOf(args));
    // Expression indexes (`CREATE INDEX ix ON t (lower(name))`) make the
    // built-in functions part of this slot's vocabulary — at their usual
    // tier 8, below the target table's columns. Cross-engine parity with the
    // sqlite / mysql / oracle index-expression slots.
    emitPgCatalogFunctions(out, args.snapshotRoutines);
    return out;
  }
  if (positionKind === 'ddl-index-method') {
    emitPgIndexMethodKeywords(out);
    return out;
  }
  if (
    positionKind === 'ddl-create-object-name'
    // ENGINE DIVERGENCE — PG's INDEX and TRIGGER names are never
    // schema-qualified (both belong to their table's schema:
    // `CREATE INDEX public.ix ON t` is a syntax error), so those two shapes
    // keep the generic fallback rather than a schema list they cannot use.
    && !isCreateIndexNameSlot(args.text.slice(ctx.statementRange.start, ctx.offset))
    && !isCreateTriggerNameSlot(args.text.slice(ctx.statementRange.start, ctx.offset))
  ) {
    // `CREATE [OR REPLACE] [MATERIALIZED] VIEW|TABLE|FUNCTION|… ▮` — the NEW
    // object's name. Only a SCHEMA can precede it, so emit schemas and nothing
    // else. PG creates a bare-named object in the FIRST search_path entry, so
    // that is the default (`public` when the snapshot carries no search_path —
    // PG_DEFAULT_SEARCH_PATH).
    emitCreateObjectNamespaces(out, snapshotSchemas, {
      defaultName: searchPath[0] ?? PG_DEFAULT_SEARCH_PATH[0],
      kind: 'schema',
      detail: 'Schema',
      format: formatIdentifier,
    });
    return out;
  }
  if (positionKind === 'ddl-fk-target') {
    // FK target — only schema-qualified table names. No keywords or
    // built-ins so the user picks a referenced table from the schema. A FK can
    // only reference a base table, never a view — exclude views/matviews.
    const fkTargetTables = snapshotTables.filter(
      (t) => t.kind !== 'view' && t.kind !== 'materialized-view',
    );
    emitTablesAndSchemas(out, fkTargetTables, snapshotSchemas, searchPath,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, fkTargetTables)
          : new Set<string>(),
    );
    return out;
  }
  if (positionKind === 'ddl-references-column-list') {
    // Cursor inside `REFERENCES <tbl>(|` — emit the REFERENCED table's
    // columns. Same shape as `ddl-index-column` but the table is extracted
    // from the REFERENCES segment rather than the `ON <tbl>` segment.
    const target = findPgReferencesTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) {
      emitPgTargetTableColumns(out, target, snapshotTables, pgPathOf(args), { pkFirst: true });
    }
    return out;
  }
  if (positionKind === 'ddl-alter-table-constraint-column-list') {
    // Cursor inside `ALTER TABLE <tbl> ... FOREIGN KEY (|` (or PRIMARY KEY /
    // UNIQUE) — emit the ALTER TABLE target's columns from the live schema.
    const target = findPgAlterTableTarget(args.text, ctx.statementRange.start, ctx.offset);
    if (target) {
      emitPgTargetTableColumns(out, target, snapshotTables, pgPathOf(args), { pkFirst: true });
    }
    return out;
  }
  if (positionKind === 'ddl-create-trigger-timing') {
    // Cursor at `CREATE [OR REPLACE] [CONSTRAINT] TRIGGER <name> |` — emit
    // the timing-keyword vocabulary AND the four DML event verbs. PG
    // accepts `BEFORE|AFTER|INSTEAD OF` followed by `INSERT|UPDATE|DELETE
    // |TRUNCATE`; surfacing both lists at this slot lets the user type the
    // event verb after the timing without losing the menu. Closes
    // goals/autocomplete-coverage pgsql script.35 §6.
    for (const kw of [
      'BEFORE',
      'AFTER',
      'INSTEAD OF',
      'INSERT',
      'UPDATE',
      'DELETE',
      'TRUNCATE',
    ]) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  // I3 parity — INSERT target slot (`INSERT INTO ▮`). The clause FSM lumps
  // this under `values`, so the INSERT body's bound-alias columns would
  // otherwise bury the relation names. Fire only once INTO has been typed
  // (`!needsInto`) — the bare `INSERT ▮` tail is ambiguous with the INSERT
  // keyword slot. INSERT targets may be updatable views, so views are kept.
  const insertSlot = isAtInsertTargetSlot(args.text, ctx.offset, ctx.statementRange.start);
  if (insertSlot && !insertSlot.needsInto) {
    emitTablesAndSchemas(out, snapshotTables, snapshotSchemas, searchPath,
      ctx.cteBindings.length === 0
        ? computeBufferReferencedTableKeys(args.text, snapshotTables)
        : new Set<string>(),
    );
    return out;
  }

  if (positionKind === 'from') {
    // Sub-position: cursor sitting after a completed relation / alias
    // (`FROM x ` / `FROM x AS y ` / `FROM x JOIN y `). Mirrors the MSSQL
    // resolver — at that spot the next legal tokens are continuation
    // keywords (WHERE / JOIN / ON / AS / ORDER / GROUP / HAVING / UNION / …)
    // and a comma would be required before another bare relation.
    const fromSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    if (
      fromSlot &&
      (fromSlot.slot === 'after-from-relation-plain' || fromSlot.slot === 'after-join-target')
    ) {
      // Generated table-alias candidate leads the list (orderHint 0); the
      // continuation keywords shift to orderHint 1+. See aliasSuggestions.ts.
      const aliasEmitted = emitAliasCandidate(out, {
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        slot: fromSlot,
        aliasBindings: ctx.aliasBindings,
        keywords: PGSQL_KEYWORDS,
      });
      emitLegalNextKeywords(out, fromSlot, aliasEmitted ? 1 : 0);
      return out;
    }
    // Any OTHER slot the classifier names at a `from` position is a boundary
    // where a relation is illegal — a set operator starting a new query branch
    // (`… FROM t UNION ▮`), a shape keyword. Emit its keywords and stop, rather
    // than falling through to the table list below.
    //
    // A slot with an EMPTY list (the word is a construct in some other dialect,
    // e.g. a table legally named `tablesample` in MySQL) counts as DECLINED —
    // it must reach the after-relation keyword fallback below, not the bare
    // relation list, which is a syntax error at a completed relation.
    const fromSlotEmitted = !!fromSlot && emitLegalNextKeywords(out, fromSlot);
    if (fromSlotEmitted) return out;
    // Null-fallback: the classifier can decline an after-relation slot it
    // can't categorize confidently (e.g. delimited-identifier edge cases). When
    // the broader `isAfterCompletedFromRelation` text-walk still says we're past
    // a completed relation, preserve the pre-classify behavior — emit the curated
    // continuation keywords rather than offering syntactically-illegal tables.
    if (
      !fromSlotEmitted &&
      isAfterCompletedFromRelation(args.text, ctx.offset, ctx.statementRange.start)
    ) {
      emitKeywords(out, PGSQL_NEXT_AFTER_FROM_RELATION);
      return out;
    }
    emitTablesAndSchemas(
      out,
      snapshotTables,
      snapshotSchemas,
      searchPath,
      unionTableKeys(
        computeFkLinkedTableKeys(
          ctx.aliasBindings,
          snapshotTables,
          snapshotForeignKeys,
          pgBindingResolver(snapshotTables, pgPathOf(args)),
        ),
        ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
      ),
    );
    // S6 — the relation already written at the caret's own binding outranks
    // the other tier-1 (FK-linked / buffer-referenced) tables.
    promoteCaretBindingRelation(out, ctx.aliasBindings, ctx.offset);
    emitCteCandidates(out, ctx);
    emitPgSetReturningFunctions(out, args.snapshotRoutines);
    if (ctx.aliasBindings.length > 0 && snapshotForeignKeys.length > 0) {
      emitFkJoinSnippets(out, ctx.aliasBindings, snapshotTables, snapshotForeignKeys, pgPathOf(args));
    }
    if (ctx.aliasBindings.length > 0 && args.looseRels.length > 0) {
      emitLooseRelJoinSnippets({
        out,
        bindings: ctx.aliasBindings,
        tables: snapshotTables,
        looseRels: args.looseRels,
        realFks: snapshotForeignKeys,
        engine: ENGINE,
        resolveBinding: pgBindingResolver(snapshotTables, pgPathOf(args)),
      });
    }
    return out;
  }

  // `CALL ▮` names a PROCEDURE — the one statement that requires one. The
  // expression arm below deliberately filters procedures out (they are not
  // invokable in an expression), so without this the only caret that needs a
  // procedure offered functions instead and buried them in the flood.
  if (isAtCallStmtRoutineRef(args.text, ctx.statementRange.start, ctx.offset)) {
    emitRoutines(out, args.snapshotRoutines.filter((r) => r.kind === 'procedure'), searchPath);
    return out;
  }

  if (positionKind === 'ddl-trigger-event') {
    // The slot seam already named this position; only the emission was
    // missing, so the resolver flooded at a caret it knew exactly.
    if (
      emitLegalNextKeywords(out, {
        slot: 'at-trigger-event',
        relationHasAlias: false,
        projectionIsStar: false,
      })
    ) {
      return out;
    }
  }

  if (
    positionKind === 'join-on' ||
    positionKind === 'where' ||
    positionKind === 'having' ||
    positionKind === 'set' ||
    positionKind === 'values' ||
    positionKind === 'returning' ||
    positionKind === 'group-by' ||
    positionKind === 'order-by' ||
    positionKind === 'select-list' ||
    positionKind === 'subquery' ||
    positionKind === 'cte-def' ||
    positionKind === 'limit-offset' ||
    positionKind === 'unknown'
  ) {
    // T18b — typed VALUES element (Docs/AutocompleteValueSlot-Plan.md). When
    // the cursor sits inside a VALUES element whose target column is
    // positively known (explicit column list; findInsertValuesTarget), the
    // resolved-target policy (T18a) replaces the generic values-position
    // emission below and short-circuits — columns/relations are structurally
    // illegal here. NO reclassification (G23): the position stays
    // positionKind 'values'; this is resolver-side dispatch only.
    if (positionKind === 'values') {
      const hit = emitTypedValuesElement(out, args, ctx);
      if (hit) return out;
    }
    // T19 — INSERT RETURNING list (D11: INSERT-context only). Emit ONLY the
    // target table's columns + scalar functions; the relation/keyword flood
    // never serves this position. UPDATE/DELETE RETURNING and the post-INTO
    // side fall through unchanged (findInsertReturningTarget rejects them).
    if (positionKind === 'returning') {
      const hit = emitInsertReturningCandidates(out, args, ctx);
      if (hit) return out;
    }
    // Position-aware continuation-keyword boundaries (`WHERE x = 1 |`,
    // `GROUP BY x |`, `ORDER BY x |`, `… ON a=b |`, `select * |`). When the
    // classifier confidently names one of these keyword-only sub-positions,
    // emit ONLY the legal ordered keywords and short-circuit so columns /
    // tables / generic keywords don't dilute the boundary. The FROM-relation
    // and join-target slots are handled by the `from` arm above, so exclude
    // them here. A `null` classification (e.g. `WHERE |`, `SELECT a, |`) falls
    // through to today's full emission unchanged.
    const kwSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    // Anywhere inside the row-limiting clause (`LIMIT ▮` … `FETCH FIRST 10 ROWS
    // ONLY ▮`) only integers, the clause's own keywords and in-scope integer
    // operands are legal — relations, columns and the general keyword list are
    // a syntax error at every one of those carets. Emit that and stop; the
    // schema emission below must not run.
    if (kwSlot && isRowLimitSlot(kwSlot.slot)) {
      return buildRowLimitOperandCandidates(out, kwSlot.slot, ENGINE);
    }
    if (
      kwSlot &&
      kwSlot.slot !== 'after-from-relation-plain' &&
      kwSlot.slot !== 'after-join-target'
    ) {
      if (emitLegalNextKeywords(out, kwSlot)) return out;
    }
    if (ctx.aliasBindings.length > 0) {
      emitAllBoundAliasColumns(out, ctx.aliasBindings, snapshotTables, ctx.cteBindings, pgPathOf(args), args.text);
      // The trigger row qualifiers themselves (`NEW` / `OLD`). Inside a
      // trigger body there is no unqualified row scope, so one of these is the
      // first token the user must type to reach a column — yet only their
      // COLUMNS were ever emitted, never the qualifier.
      emitTriggerPseudoRelationNames(
        out, args.text, ctx.statementRange, ctx.aliasBindings, ENGINE, ctx.offset,
      );
      // `MERGE … WHEN NOT MATCHED THEN INSERT (…) VALUES (▮` — the operands
      // come from the USING source, and the qualified `src.id` form is what
      // gets written. Shared policy (D9 mirror) with the mssql / oracle MERGE
      // arms; this resolver supplies the discovery. Closes pgsql script.49 §4.
      emitPgsqlMergeValuesColumns(out, args, ctx);
    }
    // Pseudo-table `excluded` (ON CONFLICT DO UPDATE) — surface the
    // target's columns when the dotted-base is `excluded.<col>`. Direct
    // `excluded.col` lookup is handled in `emitDottedAccess`. Here we
    // additionally emit the target-table columns at expression positions
    // so unqualified column refs inside `DO UPDATE SET col = excluded.col`
    // surface naturally.
    if (args.onConflictTarget) {
      emitOnConflictTargetColumns(out, args.onConflictTarget, snapshotTables, pgPathOf(args));
    }
    // T16: the S0 value-slot relation guard (plan: Docs/AutocompleteValueSlot-Plan.md
    // T3) that used to gate the two emitters below on
    // `!detectComparisonValueContext(...)` is RETIRED — every comparison-value
    // operand in where/having/join-on now classifies via the LIVE scalar-value
    // branch above (comparison-rhs, set-rhs, between-low, between-high), which
    // returns before this expression arm is ever reached.
    //
    // P7 (Docs/AutocompletePredicateHead-Plan.md): bare predicate HEADS
    // (`WHERE ▮`, `HAVING ▮`, `ON ▮`, `AND/OR/NOT ▮` continuations) no longer
    // reach this arm either — they classify 'predicate-head' and return from
    // the LIVE head branch above, where relations are FORBIDDEN_KINDS
    // (`WHERE <table>` is illegal SQL). What still lands here under
    // where/having/join-on is the recognizer's conservative refusals —
    // mid-expression boundaries (`WHERE x ▮`), `WHERE (▮` open-paren heads,
    // CASE WHEN heads, IS-chain territory (PH-D6 v1 gaps) — plus the
    // non-predicate positionKinds of this arm (select-list/unknown/…). For
    // those the full relation emission below is unchanged by design (the
    // no-worse invariant; the v1 gaps are recorded in the P10 gaps sweep).
    //
    // `UPDATE … SET ▮` is the one position in this arm where a RELATION is
    // never legal: the assignment target must be an unqualified column of the
    // update target (PG rejects even the target's own alias there — see
    // script.9 §1 in Docs/AutocompleteTestScripts/pgsql.md), and the SET RHS
    // reaches this arm only as a `set-rhs` value-slot refusal, where a bare
    // relation name is equally a syntax error. Schemas stay: a
    // schema-qualified FUNCTION call (`SET total = sales.recalc(id)`) is legal
    // on the RHS and the qualifier is the first token of it.
    // PARITY NOTE: the mysql / mssql / oracle / sqlite expression arms emit
    // the same relation list at their `set` positions and none of their spec
    // rows want it either; those four are a follow-up rather than part of this
    // change, because their coverage XFAILs pin exact head-of-list
    // fingerprints that a same-run relation removal would invalidate wholesale.
    //
    // A PL/pgSQL `RAISE … , ▮` argument is the same story one level down: a
    // procedural statement's operands are the block's locals and expressions
    // over them, never a relation. Schemas stay for the same reason — a
    // schema-qualified function call is legal in the argument.
    if (positionKind === 'set' || isPgsqlRaiseArgument(
      args.text, ctx.statementRange.start, ctx.offset,
    )) {
      emitSchemas(out, snapshotSchemas);
    } else {
      emitTablesAndSchemas(out, snapshotTables, snapshotSchemas, searchPath,
        ctx.cteBindings.length === 0
            ? computeBufferReferencedTableKeys(args.text, snapshotTables)
            : new Set<string>(),
      );
    }
    emitCteCandidates(out, ctx);
    // Expression positions (WHERE/SELECT/HAVING/…) can only invoke functions —
    // stored PROCEDUREs are callable solely via `CALL`, so filter them out
    // here. The CALL-statement path emits procedures separately.
    emitRoutines(
      out,
      args.snapshotRoutines.filter((r) => r.kind !== 'procedure'),
      searchPath,
    );
    emitPgCatalogFunctions(out, args.snapshotRoutines);
    // F5.3-R-bucket: surface SELECT-list projection aliases in HAVING /
    // ORDER-BY / GROUP-BY positions. Postgres accepts an unqualified
    // `AS`-bound alias in all three clauses.
    emitSelectListAliasCandidates(
      out,
      args.text,
      ctx.statementRange,
      ctx.offset,
      'pgsql',
      positionKind,
      (name) => formatIdentifier(name),
    );
    // At a PL/pgSQL body statement-start (`… AS $$ DECLARE … BEGIN ▮`, `…; ▮`)
    // the next token is a statement keyword or a declared local, never a schema
    // object — the same promotion the MySQL / Oracle / T-SQL arms apply, keyed
    // on the dollar-quoted body PG uses instead of a tracked compound block.
    // The tokenizer reads the open `$$` region as one string, so the boundary
    // check runs on the BODY text alone (from just past the opening tag).
    const plpgsqlBodyStart = dollarQuotedBodyStart(args.text, ctx.offset);
    const atPlpgsqlStatementStart =
      plpgsqlBodyStart !== null &&
      isAtProceduralStatementStart(
        args.text.slice(plpgsqlBodyStart, ctx.offset), ctx.offset - plpgsqlBodyStart, ENGINE,
      );
    const nextLikely = atPlpgsqlStatementStart
      ? PGSQL_PLPGSQL_STATEMENT_STARTERS
      : positionKind === 'select-list' &&
          isAfterCompletedSelectProjection(
            args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
          )
        ? PGSQL_NEXT_AFTER_SELECT_PROJECTION
        : (new Set<string>() as ReadonlySet<string>);
    emitProjectionWildcard(out, positionKind, ENGINE);
    // Tier 2 at a body statement-start (as the MySQL arm does): above the
    // tables that would otherwise bury the keywords, below the declared locals
    // at tier 1, which stay on top.
    emitKeywords(out, nextLikely, atPlpgsqlStatementStart ? 2 : 1);
    if (atPlpgsqlStatementStart) emitPlpgsqlOnlyStatementStarters(out);
    // F5.3 Wave-4 — direction + null-ordering keywords at ORDER BY position.
    // After a completed ORDER BY expression (`c.last_name <here>`), PG accepts
    // `ASC`, `DESC`, `NULLS FIRST`, `NULLS LAST`. The two-token NULLS keywords
    // aren't in the curated single-keyword list, so emit them directly.
    if (positionKind === 'order-by') {
      // After a completed sort key these are the likely next token. At the
      // HEAD of the clause a sort key is still owed, so they stay available —
      // the user may be editing an existing item — but must not outrank the
      // columns the position actually calls for.
      const afterSortKey = isAfterCompletedListItem(
        args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
      );
      for (const kw of PGSQL_ORDER_BY_DIRECTION_KEYWORDS) {
        out.push({
          label: kw,
          insertText: kw,
          isSnippet: false,
          kind: 'keyword',
          tier: afterSortKey ? 1 : 7,
          detail: 'SQL keyword',
        });
      }
    }
    // JCS Slice C3: operator suggestion is gated on the cursor's preceding
    // operand. JSON ops only surface when the LHS column is JSON-classified;
    // numeric / non-JSON columns yield no JSON operator candidates.
    emitAggregateArgHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitAggregateTailHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitTypeAwareJsonOperators(out, args);
    if (args.snapshotExtensions.includes('vector')) {
      emitPgvectorOperators(out);
    }
    // F5.2-A — drop already-typed columns from the next slot inside an
    // INSERT column-list (`INSERT INTO t (a, |)` must not re-offer `a`).
    // A column may be assigned only once in an UPDATE, so the SET list's
    // already-assigned names are illegal at the next slot.
    filterAlreadyAssignedSetColumns(
      out, args.text, ctx.offset, ctx.statementRange.start, ctx.statementRange.end, ENGINE,
    );
    if (positionKind === 'values') {
      filterAlreadyTypedInsertColumns(out, args, ctx);
      // A `SERIAL` / identity column IS a legal member of the list (PG accepts
      // an explicit value; `GENERATED ALWAYS AS IDENTITY` takes `OVERRIDING
      // SYSTEM VALUE`), so it is DEMOTED, never hidden — the writer-supplied
      // columns lead instead. Shared engine-neutral policy with the T-SQL arm.
      // A plain `DEFAULT` (`logged_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP`) is
      // deliberately NOT demoted: `hasDefault` makes a column optional, not
      // engine-owned. Closes pgsql script.25 §1/§2.
      demoteGeneratedInsertColumns(out, {
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        statementEnd: ctx.statementRange.end,
        engine: ENGINE,
        tables: args.snapshotTables,
      });
    }
    return out;
  }

  emitTablesAndSchemas(out, snapshotTables, snapshotSchemas, searchPath,
    ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
  );
  emitKeywords(out);
  return out;
}

/**
 * T18b — when the cursor sits inside a VALUES element whose target column is
 * positively known (`findInsertValuesTarget`), resolve the target column in
 * the snapshot (search-path-aware, mirrors `resolveRelation`/`foldIdentifier`
 * parity with the rest of this resolver) and emit the shared resolved-target
 * policy (T18a) — enum values (tier 1), NULL/DEFAULT keywords (D13), a
 * scalar-subquery snippet, and compat-tiered scalar routines. Returns `false`
 * (caller falls through to the unchanged generic emission) when the element
 * isn't positively typed or the target table/column can't be resolved.
 */
function emitTypedValuesElement(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  // Trigger bodies put a row scope in play (`NEW.`/`OLD.`), so columns are
  // NOT structurally illegal at VALUES elements there — the resolved-target
  // premise (`legalSources.columns: false`) fails and the generic emission
  // must serve the position. Pinned by sqlite coverage script.36 §5/§6.
  if (args.triggerHostTable) return false;
  const hit = findInsertValuesTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(hit.table.name),
    hit.table.schema ? foldIdentifier(hit.table.schema) : null,
    pgPathOf(args),
  );
  if (!tbl) return false;
  const colFolded = foldIdentifier(hit.columnName);
  const column = tbl.columns.find((c) => foldIdentifier(c.name) === colFolded);
  if (!column) return false;

  const columnMeta: ColumnMeta = {
    name: column.name,
    dataType: column.dataType,
    nullable: column.nullable ?? false,
    isPrimaryKey: column.isPrimaryKey ?? false,
    ordinal: 0,
    ...(column.enumValues && column.enumValues.length > 0
      ? { enumValues: column.enumValues }
      : {}),
  };

  const connectionId = args.connectionId;
  const database = args.database;
  const target: ResolvedValueTarget = {
    kind: 'insert-column',
    column: columnMeta,
    coarseType: coarseTypeOfDataType(column.dataType),
    legalSources: { columns: false, keywords: legalValueKeywordsFor(ENGINE, columnMeta) },
    collectEnumValues:
      connectionId && database
        ? () => {
            const tmp: CompletionCandidate[] = [];
            emitEnumValueCandidates(tmp, {
              connectionId,
              database,
              schema: tbl.schema ?? '',
              table: tbl.name,
              column: column.name,
              quote: findValueStringBodyStart(args.text, ctx.offset) === undefined,
              tier: 1,
              nativeFallbackMembers: column.enumValues,
            });
            return tmp;
          }
        : undefined,
  };

  emitResolvedValueCandidates(out, target, {
    engine: ENGINE,
    collectScalarRoutines: () => collectPgsqlScalarRoutineDescriptors(args.snapshotRoutines),
  });
  // Builtin scalar functions (now(), gen_random_uuid(), …) are legal VALUES
  // elements — same builtins-after-policy shape as the scalar-value branch
  // (C3 sol finding 2: the generic 'values' arm emitted them, so the typed
  // branch must keep them).
  emitPgCatalogFunctions(out, args.snapshotRoutines);
  return true;
}

/**
 * T19 — INSERT RETURNING list. Emits ONLY the resolved target table's
 * columns (tier 1) + scalar routines (tier 3); returns `false` (generic
 * emission serves the position) when the statement isn't an INSERT, the
 * cursor is past a RETURNING…INTO, the target can't be resolved, or a
 * trigger-body row scope is in play (`NEW.`/`OLD.` are legal in a PL/pgSQL
 * RETURNING expression list — same conservatism as `emitTypedValuesElement`).
 */
function emitInsertReturningCandidates(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  if (args.triggerHostTable) return false;
  const hit = findInsertReturningTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(hit.name),
    hit.schema ? foldIdentifier(hit.schema) : null,
    pgPathOf(args),
  );
  if (!tbl) return false;
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${tbl.schema ?? ''}.${tbl.name})`,
    });
  }
  for (const r of collectPgsqlScalarRoutineDescriptors(args.snapshotRoutines)) {
    out.push({
      label: r.label,
      insertText: r.insertText,
      isSnippet: r.insertText.includes('$'),
      kind: 'routine',
      tier: 3,
      detail: `returns ${r.returnsCoarse}`,
      triggerSignatureHelp: true,
    });
  }
  // This arm short-circuits the generic emission, so the shared projection
  // wildcard has to be added here too — `RETURNING *` is the whole-row form
  // of exactly this list.
  emitProjectionWildcard(out, 'returning', ENGINE);
  return true;
}

/** F5.2-A — drop column candidates whose label matches a column already
 *  typed earlier in the same INSERT column-list. No-op outside INSERT
 *  column-list (the helper returns null then). */
function filterAlreadyTypedInsertColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): void {
  const typed = findTypedInsertColumns(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!typed || typed.length === 0) return;
  const typedSet = new Set(typed);
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (typedSet.has(c.label.toLowerCase())) {
      out.splice(i, 1);
    }
  }
}

/**
 * PG's half of the shared MERGE-VALUES qualifier policy
 * (`resolver/mergeValuesQualified.ts`): recognise the slot, discover each
 * binding's column names through the resolver's own alias-column emitter, and
 * hand both to the shared rule.
 *
 * Column DISCOVERY has to stay here — a MERGE source is typically a subquery
 * (`USING ( SELECT … ) src`), whose projection only `emitAliasColumns` knows
 * how to infer, and the dialect's identifier folding is this module's too.
 */
function emitPgsqlMergeValuesColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): void {
  if (
    !isAtMergeValuesElement(
      args.text,
      ctx.statementRange.start,
      ctx.statementRange.end,
      ctx.offset,
      ENGINE,
    )
  ) {
    return;
  }
  const { target, sources } = partitionMergeBindings(ctx.aliasBindings);
  if (sources.length === 0) return;
  const columnNamesOf = (binding: AliasBinding): readonly string[] => {
    const scratch: CompletionCandidate[] = [];
    emitAliasColumns(
      scratch, binding, args.snapshotTables, ctx.cteBindings, pgPathOf(args), args.text,
    );
    return scratch.map((c) => c.label);
  };
  emitMergeValuesQualifiedColumns({
    out,
    sources: sources.map((binding) => ({ binding, columns: columnNamesOf(binding) })),
    targetColumns: target ? columnNamesOf(target) : [],
    formatColumn: formatIdentifier,
  });
}

// ───────────────────────────────────────────────────────────────────────────
// SQL/PGQ GRAPH_TABLE completion (P2 / Task 24)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Build candidates for a cursor inside a `GRAPH_TABLE ( … )` clause, or return
 * null when the cursor is not in one (or the snapshot has no property graphs,
 * so the feature stays inert and non-graph completion is untouched). Reuses
 * the engine-shared Task-18 `classifyGraphSlot` classifier (`GRAPH_TABLE_ENGINES`
 * already includes `postgres`) so the slot decision is byte-identical to the
 * slot seam's and to the Oracle Task-20 dispatch.
 */
function collectPgGraphCandidates(args: BuildArgs): CompletionCandidate[] | null {
  const { ctx, text, propertyGraphs } = args;
  // No graphs → today's behaviour (byte-identical). Cheap substring gate keeps
  // the tokenizer off the hot path for the overwhelming non-PGQ majority.
  if (propertyGraphs.length === 0) return null;
  const slice = text.slice(ctx.statementRange.start, ctx.offset);
  if (!/graph_table/i.test(slice)) return null;

  const tokens = tokenizeUntil(text, ctx.statementRange.start, ctx.offset, 'postgres');
  const slot = classifyGraphSlot(tokens, ctx.offset);
  if (!slot) {
    // A detached top-level position inside `GRAPH_TABLE (` that owns no
    // vocabulary slot (`GRAPH_TABLE(people_graph ▮`, `… MATCH (a) ▮`) — surface
    // the one structural keyword the grammar expects next (MATCH / COLUMNS).
    // T18 has `classifyGraphSlot` return null there so graph names aren't
    // mis-offered where only MATCH is legal.
    const kw = graphStructuralKeyword(tokens, ctx.offset);
    return kw ? [graphStructuralKeywordCandidate(kw)] : null;
  }
  const info = findGraphTableInfo(tokens);
  if (!info) return null;

  const out: CompletionCandidate[] = [];
  switch (slot.kind) {
    case 'graph-name':
      emitPgGraphNames(out, propertyGraphs);
      break;
    case 'graph-label':
      emitPgGraphLabels(out, args, info);
      break;
    case 'graph-element-property':
      emitPgGraphElementProperties(out, args, info, slot.elementVariable);
      break;
    case 'graph-columns':
      emitPgGraphColumns(out, args, info);
      break;
  }
  return out;
}

/** Resolve the property graph named after `GRAPH_TABLE (`. Null when the name
 *  is absent or matches nothing (caller falls back to all graphs). */
function resolvePgGraphByName(
  graphs: readonly PropertyGraphMeta[],
  name: string | null,
): PropertyGraphMeta | null {
  if (!name) return null;
  return graphs.find((g) => graphIdentEquals(name, g.name)) ?? null;
}

function emitPgGraphNames(
  out: CompletionCandidate[],
  graphs: readonly PropertyGraphMeta[],
): void {
  for (const g of graphs) {
    out.push({
      label: g.name,
      insertText: formatIdentifier(g.name),
      isSnippet: false,
      kind: 'table',
      tier: 2,
      detail: 'Property graph',
      filterText: g.name,
      // Bounded engine-schema population — survive the untyped cap.
      capExempt: true,
    });
  }
}

/**
 * Vertex/edge labels of the enclosing graph, filtered by whether the cursor's
 * label slot sits in a node pattern `( … )` (vertex labels) or an edge pattern
 * `-[ … ]-` (edge labels). Unknown pattern context → all labels. When the
 * graph name is unresolved, fall back to the union of every graph's labels.
 */
function emitPgGraphLabels(
  out: CompletionCandidate[],
  args: BuildArgs,
  info: GraphTableInfo,
): void {
  const { ctx, text, propertyGraphs } = args;
  const graph = resolvePgGraphByName(propertyGraphs, info.graphName);
  const source = graph ? [graph] : propertyGraphs;
  const patternCtx = graphPatternContext(text, info.bodyStart, ctx.offset);
  const seen = new Set<string>();
  for (const g of source) {
    for (const lbl of g.labels) {
      if (patternCtx === 'vertex' && lbl.kind !== 'vertex') continue;
      if (patternCtx === 'edge' && lbl.kind !== 'edge') continue;
      const key = lbl.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        label: lbl.name,
        insertText: lbl.name,
        isSnippet: false,
        kind: 'table',
        tier: 2,
        detail: lbl.kind === 'edge' ? 'Edge label' : 'Vertex label',
        filterText: lbl.name,
        capExempt: true,
      });
    }
  }
}

/**
 * Properties of the element variable's declared label(s): resolve the variable
 * → its labels via `collectGraphElementVariables` over the graph body, then
 * union the matching labels' properties within the enclosing graph. An
 * unlabeled (or unbound) variable → the union of the graph's properties.
 */
function emitPgGraphElementProperties(
  out: CompletionCandidate[],
  args: BuildArgs,
  info: GraphTableInfo,
  elementVariable: string | undefined,
): void {
  const { ctx, text, propertyGraphs } = args;
  const graph = resolvePgGraphByName(propertyGraphs, info.graphName);
  const graphs = graph ? [graph] : propertyGraphs;

  const body = text.slice(info.bodyStart, ctx.offset);
  const vars = collectGraphElementVariables(body, 'postgres');
  const bound = elementVariable
    ? vars.find((v) => graphIdentEquals(elementVariable, v.variable))
    : undefined;
  const wantLabels = bound && bound.labels.length > 0 ? bound.labels : null;

  const seen = new Set<string>();
  for (const g of graphs) {
    for (const lbl of g.labels) {
      if (
        wantLabels &&
        !wantLabels.some((w) => graphIdentEquals(w, lbl.name))
      ) {
        continue;
      }
      for (const prop of lbl.properties) {
        const key = prop.name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          label: prop.name,
          insertText: prop.name,
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: prop.dataType
            ? `${prop.dataType} (${lbl.name} property)`
            : `${lbl.name} property`,
          filterText: prop.name,
          capExempt: true,
        });
      }
    }
  }
}

/**
 * The `COLUMNS ( … )` projection slot. PG19 rejects a bare element variable
 * (`a AS x` errors) and ships no PGQ scalar functions (probe-pinned — see
 * `PG_PGQ_FUNCTIONS`), so this offers the declared element variables (the
 * `var.prop` navigation entry point) and the `AS` alias keyword only. The
 * `PG_PGQ_FUNCTIONS` loop is empty today but kept so a future PG release that
 * ships functions extends the one shared list.
 */
function emitPgGraphColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  info: GraphTableInfo,
): void {
  const { ctx, text } = args;
  const body = text.slice(info.bodyStart, ctx.offset);
  const seenVar = new Set<string>();
  for (const v of collectGraphElementVariables(body, 'postgres')) {
    const key = v.variable.toLowerCase();
    if (seenVar.has(key)) continue;
    seenVar.add(key);
    out.push({
      label: v.variable,
      insertText: v.variable,
      isSnippet: false,
      kind: 'alias',
      tier: 1,
      detail: 'Graph element variable',
      filterText: v.variable,
    });
  }
  for (const fn of PG_PGQ_FUNCTIONS) {
    out.push({
      label: fn.name,
      insertText: `${fn.name}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 3,
      detail: 'PGQ function',
      filterText: fn.name,
      triggerSignatureHelp: true,
    });
  }
  out.push({
    label: 'AS',
    insertText: 'AS',
    isSnippet: false,
    kind: 'keyword',
    tier: 7,
    detail: 'SQL keyword',
  });
}

function emitDottedAccess(out: CompletionCandidate[], args: BuildArgs): void {
  const { ctx, snapshotTables, cteBindings, onConflictTarget } = args;
  if (!ctx.dottedBase) return;
  const { parts } = ctx.dottedBase;
  if (parts.length === 0) return;
  const baseFolded = foldIdentifier(parts[0]!);

  if (parts.length === 1) {
    // `excluded.col` inside ON CONFLICT DO UPDATE — surface the target
    // table's columns. `NEW`/`OLD` (TRIGGER pseudo-tables) likewise dispatch
    // through the alias-binding branch below since `pseudoTables.ts`
    // emits them as derived bindings. PG's TRIGGER bodies live inside
    // dollar-quoted strings, so `OLD`/`NEW` typically appear inside the
    // PL/pgSQL block (handled separately).
    if (baseFolded === 'excluded' && onConflictTarget) {
      emitInsertTargetColumns(out, onConflictTarget, snapshotTables, pgPathOf(args), 'excluded');
      return;
    }
    // F5.2-CB2 — direct-form CREATE TRIGGER body (rare in PG but legal in
    // PG14+). When the cursor is inside the body region of a `CREATE [OR
    // REPLACE] TRIGGER … ON <table>` and not inside a dollar-quoted
    // function body (that case is handled in the plpgsql branch above),
    // NEW.|/OLD.| resolves to the trigger target's columns.
    if ((baseFolded === 'new' || baseFolded === 'old') && args.triggerHostTable) {
      emitPgsqlTriggerHostColumns(out, args.triggerHostTable, snapshotTables, baseFolded);
      return;
    }
    // `price public.▮` — a SCHEMA-QUALIFIED TYPE, not a relation drill. PG
    // lets a column's type name a domain / enum / composite in another schema,
    // and `deriveCompletionContext` short-circuits to `'dotted-access'` before
    // the slot seam runs, so the type slot has to be recognised HERE. Emitting
    // that schema's user types (relations are illegal as a column type) closes
    // pgsql script.39 §5/§6 + script.40 §4/§5.
    const typeSchema = findPgsqlQualifiedTypeSlotSchema(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
    );
    if (typeSchema !== null && foldIdentifier(typeSchema) === baseFolded) {
      emitPgsqlSchemaQualifiedTypes(out, args.snapshotUserTypes, baseFolded, {
        text: args.text,
        cursorOffset: ctx.offset,
      });
      return;
    }
    const aliasMatch = ctx.aliasBindings.find(
      (b) => foldIdentifier(b.alias) === baseFolded,
    );
    if (aliasMatch) {
      emitAliasColumns(out, aliasMatch, snapshotTables, cteBindings, pgPathOf(args), args.text);
      // F5.2-CB5 + S3 — FK/partner-aware column ranking at JOIN ON / WHERE
      // dotted positions (partner affinity → endpoint-pair FKs → name∩+PK).
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'postgres',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
        binding: aliasMatch,
        tables: snapshotTables,
      });
      // Workstream C pgsql 32.§6 — `GROUP BY f.▮` lifts the columns the
      // statement already projects through this alias (self-gated to the
      // group-by clause).
      applyProjectionAwareGroupByRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'postgres',
        alias: aliasMatch.alias,
      });
      // I2 parity — `jsonb_path_query(p.▮` lifts the JSON document column:
      // buffer-affinity ∪ bus-classified JSON columns (pgsql fn-set).
      if (
        isAtJsonFunctionFirstArg(
          args.text, ctx.offset, ctx.statementRange.start, PGSQL_JSON_FN_NAMES,
        )
      ) {
        applyJsonFunctionArgPromotion(
          out, collectPgsqlKnownJsonColumns(args, aliasMatch),
        );
      }
      // Array-function argument (`= ANY(f.▮`, `array_length(f.▮`) lifts the
      // array-typed columns (pgsql script.13 §2/§4) — same lift helper as the
      // JSON affinity, keyed on the bound relation's declared column types.
      if (isAtArrayFunctionArg(args.text, ctx.offset, ctx.statementRange.start)) {
        applyJsonFunctionArgPromotion(out, collectPgsqlArrayColumns(args, aliasMatch));
      }
      return;
    }
    const cteMatch = ctx.cteBindings.find((c) => foldIdentifier(c.name) === baseFolded);
    if (cteMatch && !isInOwnNonRecursiveBody(cteMatch, ctx.offset)) {
      emitCteColumnList(out, cteMatch, {
        source: args.text,
        engineId: 'pgsql',
        snapshotTables,
        cteBindings,
      });
      // S3 partner affinity for CTE projections (`ON child.manager_id = ec.▮`
      // wants the FK pair `employee_id`) — name-based, no FK metadata on
      // the CTE itself is needed.
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'postgres',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
      });
      return;
    }
    // `FOR r_row IN SELECT … LOOP` — the loop RECORD's fields are exactly the
    // loop query's projection, and PL/pgSQL rejects anything else at runtime.
    // The record is not an alias binding (it has no relation) and not a CTE, so
    // without this the caret fell through to the schema drill and answered with
    // nothing. Shared with the Oracle FOR-IN extractor. Closes pgsql
    // script.37 §7.
    if (args.plpgsqlBlock) {
      // Scanned over the BODY, not the buffer: to the tokenizer the whole
      // `$$ … $$` is one string token, so a whole-buffer walk sees no `FOR`
      // at all. (The Oracle twin has no dollar-quoting and passes the buffer.)
      const { bodyStart, bodyEnd } = args.plpgsqlBlock;
      const forInRecord = findProceduralForInBindings(
        args.text.slice(bodyStart, bodyEnd), ctx.offset - bodyStart, ENGINE,
      ).get(baseFolded);
      if (forInRecord) {
        for (const name of forInRecord.columns) {
          out.push({
            label: name,
            insertText: formatIdentifier(name),
            isSnippet: false,
            kind: 'column',
            tier: 1,
            detail: 'loop record field',
            filterText: name,
          });
        }
        return;
      }
    }
    // schema.table — emit tables in the named schema, narrowed to the ONE
    // relation kind the statement admits when it admits only one:
    // `ALTER TABLE public.▮` / `DROP TABLE public.▮` / `TRUNCATE public.▮`
    // take a base table (a view there is a syntax error — pgsql script.38
    // §1/§2/§5), while `REFRESH MATERIALIZED VIEW public.▮` takes a matview
    // and nothing else (script.32 §7). `COMMENT ON TABLE public.▮` names the
    // same `TABLE` keyword but accepts all three, so the classifier reads the
    // statement's LEADING keywords, not the token in front of the qualifier.
    const drillKind = classifyPgRelationDrillKind(
      args.text,
      ctx.statementRange.start,
      ctx.offset,
    );
    emitTablesInSchema(out, snapshotTables, baseFolded, drillKind);
    // A matview this script CREATEs is not in the snapshot yet (its DDL has
    // not executed), and `REFRESH MATERIALIZED VIEW public.▮` right after the
    // `CREATE MATERIALIZED VIEW` is exactly that caret — same in-buffer rule
    // the CREATE TABLE merge and `scanBufferCreatedRoutines` already apply.
    if (drillKind === 'materialized-view') {
      const emitted = new Set(out.map((c) => c.label.toLowerCase()));
      for (const mv of scanBufferCreatedMatviews(args.text)) {
        const schemaFolded = (mv.schema ?? args.searchPath[0] ?? 'public').toLowerCase();
        if (schemaFolded !== baseFolded || emitted.has(mv.name.toLowerCase())) continue;
        out.push({
          label: formatIdentifier(mv.name),
          insertText: formatIdentifier(mv.name),
          isSnippet: false,
          kind: 'view',
          tier: 1,
          detail: 'Materialized view (this script)',
          filterText: mv.name,
        });
      }
    }
    // …and its ROUTINES. A schema qualifier in front of a function call is the
    // one way to reach a function that is not on the search_path, and it is how
    // every `public.<fn>(…)` in a script is written — yet this drill used to
    // answer with relations only, so the name the user was reaching for was
    // never in the list. Emitted at tier 3 (the same tier `emitRoutines` gives
    // an in-path routine) so relations still lead. Suppressed at the
    // relation-only DDL drills, where a function name is a syntax error.
    // Closes pgsql script.35 §8 + script.44 §12 + script.45 §6.
    if (!isPgsqlRelationOnlyDrill(args.text, ctx.statementRange.start, ctx.offset)) {
      const executeFunctionDrill = isPgsqlExecuteFunctionDrill(
        args.text, ctx.statementRange.start, ctx.offset,
      );
      for (const r of args.snapshotRoutines) {
        if ((r.schema ?? PG_DEFAULT_SEARCH_PATH[0] ?? '').toLowerCase() !== baseFolded) continue;
        // `CREATE TRIGGER … EXECUTE FUNCTION public.▮` accepts ONLY a
        // `RETURNS trigger` function; the rest of the schema's functions are
        // legal syntax but a guaranteed runtime error, so they drop behind.
        const triggerReturning = (r.returns ?? '').trim().toLowerCase() === 'trigger';
        out.push({
          label: r.name,
          insertText: `${r.name}($0)`,
          isSnippet: true,
          kind: 'routine',
          tier: executeFunctionDrill && triggerReturning ? 1 : 3,
          detail: `${r.kind} (${baseFolded})`,
          filterText: r.name,
          triggerSignatureHelp: true,
        });
      }
      // …plus the routines THIS SCRIPT creates before the caret
      // (`CREATE FUNCTION public.f … ; … public.▮`): the snapshot cannot know
      // them until the DDL has run. Tier 1 — the function the script just
      // wrote is almost always the one being reached for (script.35 §8,
      // 44 §12, 45 §6). Same rule as the in-buffer CREATE TABLE merge.
      const emittedRoutines = new Set(
        out.filter((c) => c.kind === 'routine').map((c) => c.label.toLowerCase()),
      );
      for (const r of scanBufferCreatedRoutines(args.text, ctx.offset)) {
        const schemaLower = (r.schema ?? args.searchPath[0] ?? PG_DEFAULT_SEARCH_PATH[0] ?? 'public').toLowerCase();
        if (schemaLower !== baseFolded || emittedRoutines.has(r.name.toLowerCase())) continue;
        out.push({
          label: r.name,
          insertText: `${r.name}($0)`,
          isSnippet: true,
          kind: 'routine',
          tier: 1,
          detail: `${r.kind} (${baseFolded}, this script)`,
          filterText: r.name,
          triggerSignatureHelp: true,
        });
      }
    }
    // Workstream C pgsql 30.§5 — `FOREIGN KEY (film_id) REFERENCES
    // public.▮`: the FK source-column stems name the likely target table;
    // lift it above the alphabetical schema listing.
    applyReferencesTargetPromotion(
      out,
      findFkSourceColumnStems(args.text, ctx.offset, ctx.statementRange.start),
    );
    // F5.3 Wave-4 — fallback for system schemas the bus doesn't carry.
    // Surface the documented table set for information_schema / pg_catalog
    // (matches MySQL's MYSQL_SYSTEM_SCHEMA_TABLES approach). Closes
    // goals/autocomplete-coverage pgsql script.23 §3 and §5.
    const sysTables = PG_SYSTEM_SCHEMA_TABLES[baseFolded];
    if (sysTables) {
      for (const t of sysTables) {
        out.push({
          label: t,
          insertText: formatIdentifier(t),
          isSnippet: false,
          kind: 'table',
          tier: 2,
          detail: `${baseFolded} system table`,
        });
      }
    }
    return;
  }

  // Two-part `database.schema.` or `schema.table.`. PG doesn't have the
  // 3-part `db.schema.table` MSSQL form (cross-database queries require an
  // explicit FDW), so we treat any 2-part dotted base as `schema.table.<col>`.
  if (parts.length >= 2) {
    const part0Folded = foldIdentifier(parts[0]!);
    const part1Folded = foldIdentifier(parts[1]!);
    const tbl = snapshotTables.find(
      (t) =>
        (t.schema ?? '').toLowerCase() === part0Folded &&
        t.name.toLowerCase() === part1Folded,
    );
    if (tbl) {
      for (const c of tbl.columns) {
        out.push({
          label: c.name,
          insertText: formatIdentifier(c.name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${c.dataType} (${tbl.schema ?? ''}.${tbl.name})`,
        });
      }
    }
  }
}

function emitTablesInSchema(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  schemaFolded: string,
  /** When set, only relations of this `pg_class` kind are legal at the caret
   *  (see `classifyPgRelationDrillKind`). */
  onlyKind: PgDrillRelationKind | null = null,
): void {
  for (const t of tables) {
    if ((t.schema ?? '').toLowerCase() !== schemaFolded) continue;
    // A snapshot that predates the `pg_class` relkind scan (or one built while
    // the catalog probe failed) carries `kind: 'table'` for every relation —
    // filtering then behaves exactly as it did before the kinds existed.
    if (onlyKind !== null && t.kind !== onlyKind) continue;
    const isViewLike = t.kind === 'view' || t.kind === 'materialized-view';
    out.push({
      label: formatIdentifier(t.name),
      insertText: formatIdentifier(t.name),
      isSnippet: false,
      kind: isViewLike ? 'view' : 'table',
      tier: 2,
      detail: t.kind === 'materialized-view' ? 'Materialized view' : isViewLike ? 'View' : 'Table',
      filterText: t.name,
    });
  }
}

/**
 * The relations whose BARE name resolves to themselves through `search_path`.
 *
 * A relation qualifies when its schema is on the path and no schema EARLIER on
 * the path owns a relation with the same folded name — that earlier one would
 * win the unqualified lookup, so writing the later relation bare would silently
 * reference the wrong table. Out-of-path and shadowed relations keep the
 * `schema.name` form.
 *
 * Why it matters (see `makeTableCandidate`): the bare name is BOTH the label
 * and the insert text for these, so the completion inserts exactly the text a
 * Postgres user writes by hand.
 */
function computeBareResolvableKeys(
  tables: ReadonlyArray<SnapshotTable>,
  searchPath: readonly string[],
): ReadonlySet<string> {
  const pathIndex = new Map<string, number>();
  searchPath.forEach((s, i) => {
    const folded = s.toLowerCase();
    if (!pathIndex.has(folded)) pathIndex.set(folded, i);
  });
  // Folded relation name → the smallest search_path index that owns it.
  const winner = new Map<string, number>();
  for (const t of tables) {
    const idx = pathIndex.get((t.schema ?? '').toLowerCase());
    if (idx === undefined) continue;
    const name = foldIdentifier(t.name);
    const prev = winner.get(name);
    if (prev === undefined || idx < prev) winner.set(name, idx);
  }
  const out = new Set<string>();
  for (const t of tables) {
    const idx = pathIndex.get((t.schema ?? '').toLowerCase());
    if (idx === undefined) continue;
    if (winner.get(foldIdentifier(t.name)) === idx) out.add(fkLinkedTableKey(t));
  }
  return out;
}

/**
 * ENGINE DIVERGENCE — relation LABELS.
 *
 * A relation reachable unqualified through `search_path` is labelled (and
 * inserted) by its BARE name; everything else keeps `schema.name`. This is the
 * MySQL convention (`makeTableCandidate` there labels a table of the CURRENT
 * database bare and only qualifies cross-database ones) transposed onto the PG
 * namespace mechanism, whose unit of "current" is the search_path rather than
 * the database. SQLite has no schema layer and is bare for the same reason.
 *
 * MSSQL deliberately does NOT follow: T-SQL has no search_path, `dbo` is not
 * implicit for every principal, and the idiomatic reference is two-part, so
 * `mssqlResolver.makeTableCandidate` keeps `schema.name` for every relation.
 *
 * The three engine specs agree with their resolvers —
 * `Docs/AutocompleteTestScripts/pgsql.md` anchors relations bare at every
 * `from` / `dotted-access` checkpoint (`customer`, `customer_list`,
 * `acact_film_promo`), while `mssql.md` anchors them as `dbo.<name>`.
 */
function makeTableCandidate(
  t: SnapshotTable,
  searchPath: readonly string[],
  fkLinked = false,
  bareResolvable = false,
): CompletionCandidate {
  const schema = t.schema ?? '';
  // System schemas demote to tier 8.
  const isSystem = PG_SYSTEM_SCHEMAS.has(schema.toLowerCase());
  // `pg_catalog` is implicitly on every search_path, but its relations are
  // still shown (and inserted) qualified: an unqualified `pg_class` reads as a
  // user table at a glance, and the qualifier is what makes a catalog
  // reference legible.
  const bare = bareResolvable && !isSystem && !!schema;
  const insertText = bare || !schema
    ? formatIdentifier(t.name)
    : formatQualifiedTwoPart(schema, t.name);
  // Tables in search_path schemas sit at tier 2; tables in out-of-path
  // schemas demote to tier 4 (other-visible-schemas) so users see their
  // current-search-path tables on top. FK-linked tables (an FK edge
  // connects them to a bound relation) promote to tier 1 — the
  // actually-joinable candidates at a JOIN slot.
  const inPath = searchPath.includes(schema.toLowerCase());
  const tier: CompletionCandidate['tier'] = isSystem ? 8 : fkLinked ? 1 : inPath ? 2 : 4;
  const detailPrefix = isSystem ? 'System ' : '';
  // A matview reads as a view at every reference position (you SELECT from it,
  // you cannot INSERT into it), so it takes the view icon and the view kind —
  // the one place the distinction matters is the `REFRESH MATERIALIZED VIEW`
  // drill, which filters on `TableMeta.kind` before reaching here.
  const isViewLike = t.kind === 'view' || t.kind === 'materialized-view';
  return {
    label: bare || !schema ? t.name : `${schema}.${t.name}`,
    insertText,
    isSnippet: false,
    kind: isViewLike ? 'view' : 'table',
    tier,
    detail: isViewLike
      ? `${detailPrefix}${t.kind === 'materialized-view' ? 'Materialized view' : 'View'}`
      : `${detailPrefix}Table`,
    filterText: t.name,
  };
}

/**
 * Emit the curated PG data type list at a "data-type" slot (CREATE TABLE
 * column-type, ALTER TABLE ADD COLUMN type, CREATE FUNCTION parameter /
 * RETURNS type, CAST(...) target, `::`-cast suffix). Tier 1 so types
 * outrank tables / columns / keywords at this slot. Two-token entries
 * like `DOUBLE PRECISION` are emitted as-is.
 *
 * `buffer` adds the types the SCRIPT ITSELF created above the caret — see
 * `emitPgsqlBufferTypes`.
 */
function emitPgsqlDataTypeCandidates(
  out: CompletionCandidate[],
  userTypes: readonly SnapshotUserType[] = [],
  buffer?: { readonly text: string; readonly cursorOffset: number },
): void {
  // User-defined enum types (`mpaa_rating`) rank above the built-ins — they're
  // the ones the user actually declared in this database. Emitted verbatim
  // (lowercase `pg_type.typname`) so the inserted text is a valid type ref.
  const seen = new Set<string>();
  for (const t of userTypes) {
    const lower = t.name.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push({
      label: t.name,
      insertText: t.name,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: userTypeDetail(t),
    });
  }
  // A bare type slot names the type without a qualifier, so the in-buffer
  // types are deduped on the NAME alone (a same-named type in another schema
  // would insert the same text here).
  if (buffer) {
    emitPgsqlBufferTypes(out, buffer, (t) => {
      const lower = t.name.toLowerCase();
      if (seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });
  }
  for (const type of getPgsqlDataTypes()) {
    out.push({
      label: type,
      insertText: type,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'PG data type',
    });
  }
}

/**
 * The user-defined types that live in ONE schema — the answer at a
 * schema-qualified type slot (`price public.▮`). Only user types are emitted:
 * a built-in like `integer` lives in `pg_catalog` and would be wrong after a
 * `public.` qualifier, and a relation is not legal as a column type at all
 * (which is what this caret used to answer with).
 *
 * `buffer` adds the types the SCRIPT ITSELF created above the caret — see
 * `emitPgsqlBufferTypes`.
 */
function emitPgsqlSchemaQualifiedTypes(
  out: CompletionCandidate[],
  userTypes: readonly SnapshotUserType[],
  schemaFolded: string,
  buffer?: { readonly text: string; readonly cursorOffset: number },
): void {
  const seen = new Set<string>();
  for (const t of userTypes) {
    // A type whose schema the snapshot did not record is assumed to be in the
    // connection's default namespace — dropping it would show an empty list
    // where the user has types.
    const typeSchema = (t.schema ?? PG_DEFAULT_SEARCH_PATH[0] ?? 'public').toLowerCase();
    if (typeSchema !== schemaFolded) continue;
    seen.add(t.name.toLowerCase());
    out.push({
      label: t.name,
      insertText: t.name,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: userTypeDetail(t),
    });
  }
  if (buffer) {
    emitPgsqlBufferTypes(out, buffer, (t) => {
      // Same default-namespace assumption as above, applied to an unqualified
      // `CREATE DOMAIN acact_email` in the script.
      const typeSchema = (t.schema ?? PG_DEFAULT_SEARCH_PATH[0] ?? 'public').toLowerCase();
      if (typeSchema !== schemaFolded) return false;
      const lower = t.name.toLowerCase();
      if (seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });
  }
}

/**
 * The domains / enums / range types / composites this SCRIPT created above the
 * caret, at the same tier 1 as the snapshot's own types with `(this script)`
 * on the detail line to say where they came from.
 *
 * The schema snapshot is built from the live database, so a type whose
 * `CREATE DOMAIN` / `CREATE TYPE` sits ten lines up — inside the `DO $$ … $$`
 * idempotency wrapper, typically — cannot be in it yet, and that type is
 * exactly the one the `price public.▮` caret below is reaching for
 * (pgsql script.39 §5/§6, script.40 §4/§5). `accept` is the caller's
 * schema filter + dedupe against what it already emitted.
 */
function emitPgsqlBufferTypes(
  out: CompletionCandidate[],
  buffer: { readonly text: string; readonly cursorOffset: number },
  accept: (t: InBufferType) => boolean,
): void {
  for (const t of scanBufferCreatedTypes(buffer.text, buffer.cursorOffset)) {
    if (!accept(t)) continue;
    // The scan hands back the name UNQUOTED and in its written case, so a
    // `CREATE DOMAIN public."My Domain"` needs its quotes back to insert as a
    // valid type reference (the snapshot path has no such need — PG stores
    // `pg_type.typname` already folded).
    out.push({
      label: formatIdentifier(t.name),
      insertText: formatIdentifier(t.name),
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: `user-defined ${t.kind} type (this script)`,
      filterText: t.name,
    });
  }
}

/** `enum` / `domain` / `range` / `composite` detail line for a user type; the
 *  bare fallback covers snapshots built before the `pg_type` kind scan. */
function userTypeDetail(t: SnapshotUserType): string {
  return t.kind ? `user-defined ${t.kind} type` : 'user-defined type';
}

/**
 * Schema candidates only — the namespace half of `emitTablesAndSchemas`.
 *
 * Split out for the one position that admits a schema qualifier but NOT a bare
 * relation: an `UPDATE … SET ▮` assignment target. See the call site.
 */
function emitSchemas(
  out: CompletionCandidate[],
  schemas: readonly string[],
): void {
  // F5.3 Wave-4 — Postgres system schemas (`pg_catalog`, `information_schema`)
  // are universally available regardless of search_path. Snapshot
  // introspection currently skips them, so emit them as known system
  // schemas at tier 4 alongside user schemas. This makes `SELECT … FROM
  // pg_catalog.<here>` and `… information_schema.<here>` discoverable.
  const seen = new Set(schemas.map((s) => s.toLowerCase()));
  const allSchemas = [...schemas];
  for (const sys of ['pg_catalog', 'information_schema']) {
    if (!seen.has(sys)) allSchemas.push(sys);
  }
  for (const s of allSchemas) {
    out.push({
      label: formatIdentifier(s),
      insertText: formatIdentifier(s),
      isSnippet: false,
      kind: 'schema',
      tier: 4,
      detail: 'Schema',
      filterText: s,
      // The engine-shipped system schemas (module-level PG_SYSTEM_SCHEMAS)
      // share the unbounded 'schema' kind — flag so the ranker's untyped cap
      // can't slice them on a >500-candidate pool (Plan 15 Gate D round 2
      // class kill; the oracle twin was script.24 §2's data-dictionary
      // synonyms). User schemas (schema-derived) stay cappable.
      ...(PG_SYSTEM_SCHEMAS.has(s.toLowerCase()) ? { capExempt: true } : {}),
    });
  }
}

function emitTablesAndSchemas(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  schemas: readonly string[],
  searchPath: readonly string[],
  fkLinkedKeys?: ReadonlySet<string>,
): void {
  emitSchemas(out, schemas);
  const bareKeys = computeBareResolvableKeys(tables, searchPath);
  for (const t of tables) {
    const key = fkLinkedTableKey(t);
    out.push(
      makeTableCandidate(t, searchPath, fkLinkedKeys?.has(key) ?? false, bareKeys.has(key)),
    );
  }
}

// F5.2-CB8: PG semantics — a CTE may only self-reference inside its own
// body when the WITH clause is RECURSIVE. Returns true when the cursor sits
// inside a non-recursive CTE's body, so the resolver must suppress that
// binding (table candidate AND dotted-access column list).
function isInOwnNonRecursiveBody(cte: CteBinding, offset: number): boolean {
  if (cte.recursive) return false;
  if (!cte.bodyRange) return false;
  return offset >= cte.bodyRange.start && offset <= cte.bodyRange.end;
}

function emitCteCandidates(out: CompletionCandidate[], ctx: BuildArgs['ctx']): void {
  for (const cte of ctx.cteBindings) {
    if (isInOwnNonRecursiveBody(cte, ctx.offset)) continue;
    out.push({
      label: cte.name,
      insertText: cte.name,
      isSnippet: false,
      kind: 'cte',
      tier: 5,
      detail: 'CTE',
      filterText: cte.name,
    });
  }
}

function emitRoutines(
  out: CompletionCandidate[],
  routines: readonly SnapshotRoutine[],
  searchPath: readonly string[],
): void {
  for (const r of routines) {
    const schema = r.schema ?? '';
    const inPath = searchPath.includes(schema.toLowerCase());
    out.push({
      label: r.name,
      insertText: `${r.name}($0)`,
      isSnippet: true,
      kind: 'routine',
      tier: inPath ? 3 : 4,
      detail: schema ? `${r.kind} (${schema})` : (r.kind ?? 'routine'),
      filterText: r.name,
      triggerSignatureHelp: true,
    });
  }
}

/**
 * Emit pg_catalog built-in functions from the curated list. Deduped against
 * user-routine candidates by lower-cased name so a user-defined function
 * that shadows a built-in surfaces only once. Tier 8 (system-function) so
 * they sit at the bottom of the suggestion list — power users still find
 * them via fuzzy match.
 */
/**
 * F5.3 R-bucket — emit PostgreSQL set-returning functions at FROM position.
 * Closes script.21 §2: `generate_series`, `regexp_split_to_table`, `unnest`,
 * `jsonb_array_elements`, `json_each`, etc. Tier 8 keeps them below tables.
 */
function emitPgSetReturningFunctions(
  out: CompletionCandidate[],
  userRoutines: readonly SnapshotRoutine[],
): void {
  const userNames = new Set<string>();
  for (const r of userRoutines) userNames.add(r.name.toLowerCase());
  for (const fn of getPgSetReturningFunctions()) {
    if (userNames.has(fn)) continue;
    out.push({
      label: fn,
      insertText: `${fn}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 8,
      detail: 'set-returning function',
      filterText: fn,
      triggerSignatureHelp: true,
    });
  }
}

function emitPgCatalogFunctions(
  out: CompletionCandidate[],
  userRoutines: readonly SnapshotRoutine[],
): void {
  const userNames = new Set<string>();
  for (const r of userRoutines) userNames.add(r.name.toLowerCase());
  for (const fn of getPgBuiltinFunctions()) {
    if (userNames.has(fn)) continue;
    out.push({
      label: fn,
      insertText: `${fn}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 8,
      detail: 'pg_catalog function',
      filterText: fn,
      triggerSignatureHelp: true,
    });
  }
}

// Emit the grammatically-legal continuation keywords for a recognized cursor
// sub-position, ordered by popularity (orderHint = index). Returns true when
// it emitted (caller should then return — these are keyword-only boundaries).
function emitLegalNextKeywords(
  out: CompletionCandidate[],
  slot: NextKeywordSlotResult,
  // Shifts the popularity order when a leading candidate (the generated
  // table alias) already occupies orderHint 0.
  orderHintBase = 0,
): boolean {
  const kws = getLegalNextKeywords(ENGINE, slot.slot, {
    relationHasAlias: slot.relationHasAlias,
    projectionIsStar: slot.projectionIsStar,
  });
  if (kws.length === 0) return false;
  // The slot IS answered whenever it has a vocabulary, so `true` (the caller's
  // short-circuit) does not depend on how many items are new — a keyword an
  // earlier, more specific emission already offered (a compound remainder at
  // `NULLS F▮`) is skipped rather than repeated in the widget.
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  kws.forEach((kw, i) => {
    if (alreadyOffered.has(kw.toUpperCase())) return;
    out.push({ label: kw, insertText: kw, isSnippet: false, kind: 'keyword', tier: 1, detail: 'SQL keyword', orderHint: orderHintBase + i });
  });
  return true;
}

/** PL/pgSQL control statements the general keyword list does not carry. */
function emitPlpgsqlOnlyStatementStarters(out: CompletionCandidate[]): void {
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  for (const kw of PLPGSQL_ONLY_STATEMENT_STARTERS) {
    if (alreadyOffered.has(kw)) continue;
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 2,
      detail: 'PL/pgSQL keyword',
    });
  }
}

function emitKeywords(
  out: CompletionCandidate[],
  nextLikely: ReadonlySet<string> = new Set(),
  promotedTier: CompletionCandidateTier = 1,
): void {
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  for (const kw of getPgsqlKeywordsForPosition()) {
    // A more specific emission already offered this keyword (e.g. a compound
    // remainder at tier 1) — don't repeat it in the widget.
    if (alreadyOffered.has(kw)) continue;
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: nextLikely.has(kw) ? promotedTier : 7,
      detail: 'SQL keyword',
    });
  }
}

/**
 * F5.3 Wave-4 — extract the target table from the `ON <schema>.<tbl>` (or
 * bare `ON <tbl>`) segment of a `CREATE INDEX ... ON <tbl> [USING <method>] (`
 * statement. Returns `{ schema, name }` folded as PostgreSQL reads them — a
 * bare part lower-cased, a delimited one unquoted with its case kept, so
 * `"Orders"` still names `"Orders"` and not its sibling `orders` — or null
 * when the segment isn't recognisable. Same for the ALTER TABLE / REFERENCES
 * readers below.
 */
function findPgIndexTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const m = slice.match(
    /\bON\s+(?:("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|[A-Za-z_][\w$]*)(?:\s+USING\s+[A-Za-z_][\w$]*)?\s*\(/i,
  );
  if (!m || !m[2]) return null;
  const schemaRaw = m[1] ?? null;
  const nameRaw = m[2]!;
  return {
    schema: schemaRaw ? foldIdentifier(schemaRaw) : null,
    name: foldIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — extract the ALTER TABLE target from `ALTER TABLE <schema>.<tbl>`
 * in the cursor's statement preamble. Used by the
 * `ddl-alter-table-constraint-column-list` slot to surface the altered
 * table's columns at PRIMARY KEY / UNIQUE / FOREIGN KEY source slots.
 */
function findPgAlterTableTarget(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  const m = slice.match(
    // Trailing `(?![\w$])` (not `\b`): a closing `"` is a non-word char, so
    // `\b` never matched after a delimited target followed by whitespace.
    /\bALTER\s+TABLE\s+(?:(?:IF\s+EXISTS\s+)?)(?:("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|[A-Za-z_][\w$]*)(?![\w$])/i,
  );
  if (!m || !m[2]) return null;
  const schemaRaw = m[1] ?? null;
  const nameRaw = m[2]!;
  return {
    schema: schemaRaw ? foldIdentifier(schemaRaw) : null,
    name: foldIdentifier(nameRaw),
  };
}

/**
 * F5.3 Wave-4 — extract the REFERENCED table from a `REFERENCES <schema>.<tbl>(`
 * segment immediately preceding the cursor's unclosed `(`. Returns the folded
 * `{ schema, name }` pair or null if the segment is unrecognisable. Used to
 * populate `ddl-references-column-list` with the right column set.
 */
function findPgReferencesTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): { schema: string | null; name: string } | null {
  const slice = text.slice(statementStart, cursorOffset);
  // Walk back to the LAST `REFERENCES <ident>(` segment so multiple FK clauses
  // in the same statement don't fight each other.
  const re = /\bREFERENCES\s+(?:("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|[A-Za-z_][\w$]*)\s*\(/gi;
  let m: RegExpExecArray | null = null;
  let last: RegExpExecArray | null = null;
  while ((m = re.exec(slice)) !== null) last = m;
  if (!last || !last[2]) return null;
  const schemaRaw = last[1] ?? null;
  const nameRaw = last[2]!;
  return {
    schema: schemaRaw ? foldIdentifier(schemaRaw) : null,
    name: foldIdentifier(nameRaw),
  };
}

function emitPgTargetTableColumns(
  out: CompletionCandidate[],
  target: { schema: string | null; name: string },
  tables: ReadonlyArray<SnapshotTable>,
  path: PgSearchPath,
  opts?: { pkFirst?: boolean },
): void {
  // The relation PostgreSQL resolves the (folded) target to — through the
  // search_path; off it only when the path is a guess (`resolvePgRelation`).
  const tbl = resolveRelation(tables, target.name, target.schema, path);
  if (!tbl) return;
  for (const c of tbl.columns) {
    // pkFirst (REFERENCES / constraint column-lists): the referenced table's
    // PK is what an FK points at in almost every case — keep it at tier 1
    // and drop the rest to tier 2 (mirrors the MSSQL resolver).
    const pk = c.isPrimaryKey === true;
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: opts?.pkFirst && !pk ? 2 : 1,
      detail: opts?.pkFirst && pk ? `${c.dataType} (PK, ${tbl.name})` : `${c.dataType} (${tbl.name})`,
    });
  }
}

/**
 * F5.3 Wave-4 — emit Postgres access-method keywords at `CREATE INDEX … USING |`.
 * Tier 1 so the access-method vocabulary tops the suggest list.
 */
const PG_INDEX_METHODS: readonly string[] = ['btree', 'hash', 'gin', 'gist', 'brin', 'spgist'];

function emitPgIndexMethodKeywords(out: CompletionCandidate[]): void {
  for (const m of PG_INDEX_METHODS) {
    out.push({
      label: m,
      insertText: m,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'PostgreSQL index access method',
      filterText: m,
    });
  }
}

/**
 * Emit FK-driven JOIN snippet candidates. For each candidate table in the
 * snapshot that has at least one FK edge to/from a bound alias, generate
 * one snippet per applicable edge. The ranker promotes `fkJoin: true`
 * candidates to tier 1 when `positionKind === 'from'` and at least one
 * alias is bound; outside that context they fall to tier 6 so misplaced
 * snippets cannot outrank real columns or relations.
 *
 * Skips bindings tagged `kind ∈ {cte, subquery, derived}` — those have no
 * relation name to look up in the FK graph. Edges match by relation identity
 * (`fkEndpointMatch.ts`): the bound relation is the table PostgreSQL resolves
 * it to (search_path), and an edge's endpoint names its exact catalog table —
 * `"Orders"` never takes the FKs of `orders`. Dedupes by
 * `${alias}|${candidateSchema}.${candidateName}|${fkName}` (spellings kept, so
 * case-distinct tables stay two candidates) so a binding matching multiple
 * snapshot rows doesn't double-emit. Mirrors the MSSQL resolver shape.
 */
function emitFkJoinSnippets(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  fks: readonly FkEdge[],
  path: PgSearchPath,
): void {
  const existingAliases = new Set<string>(bindings.map((b) => b.alias.toLowerCase()));
  const seen = new Set<string>();
  // Same bare-vs-qualified rule as `makeTableCandidate`: a snippet for a
  // search_path relation reads `staff ON …`, not `public.staff ON …`, so the
  // template sorts next to its bare table (pgsql script.20 §1) and inserts
  // what the user would have typed.
  const bareKeys = computeBareResolvableKeys(tables, path.schemas);
  const match = makeFkEndpointMatcher(tables, pgBindingResolver(tables, path));
  // One index per pass — never cached across requests (`fkEdgeIndex.ts`).
  const applicableFks = makeApplicableFksFinder(fks, match);
  for (const binding of bindings) {
    if (binding.kind === 'cte' || binding.kind === 'subquery' || binding.kind === 'derived') {
      continue;
    }
    for (const t of tables) {
      const candidate: TableMeta = {
        name: t.name,
        schema: t.schema,
        database: t.database,
        kind: (t.kind === 'view' || t.kind === 'materialized-view'
          ? 'view'
          : 'table') as TableMeta['kind'],
        columns: [],
      };
      const applicable = applicableFks(binding, candidate);
      for (const fk of applicable) {
        const dedupeKey = `${binding.alias.toLowerCase()}|${t.schema ?? ''}.${t.name}|${fk.name ?? `${fk.fromTable}->${fk.toTable}`}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push(
          generateFkJoinSnippet({
            from: binding,
            candidate,
            fk,
            existingAliases,
            engine: ENGINE,
            match,
            bareRelation:
              bareKeys.has(fkLinkedTableKey(t)) && !PG_SYSTEM_SCHEMAS.has((t.schema ?? '').toLowerCase()),
          }),
        );
      }
    }
  }
}

/** The table a bound relation names, as PostgreSQL resolves it — FK / loose-FK JOIN identity. */
function pgBindingResolver(
  tables: ReadonlyArray<SnapshotTable>,
  path: PgSearchPath,
): (binding: AliasBinding) => SnapshotTable | undefined {
  return (b) =>
    resolveRelation(tables, foldIdentifier(b.relation), b.schema ? foldIdentifier(b.schema) : null, path);
}

function emitAllBoundAliasColumns(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  path: PgSearchPath,
  text: string,
): void {
  for (const b of bindings) {
    emitAliasColumns(out, b, tables, cteBindings, path, text);
  }
}

/**
 * I2 parity — the column set treated as JSON at a `jsonb_path_query(p.▮`
 * slot: buffer-affinity (columns this buffer already uses as a JSON-fn first
 * arg) ∪ the binding's bus-classified JSON columns. Lower-cased — the affinity
 * module compares case-insensitively. MetaInfo-declared columns are omitted
 * (BuildArgs carries no connectionId); the buffer signal covers the common
 * in-query case.
 */
function collectPgsqlKnownJsonColumns(
  args: BuildArgs,
  binding: AliasBinding,
): Set<string> {
  const cols = collectBufferJsonFunctionColumns(args.text, PGSQL_JSON_FN_NAMES);
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(binding.relation),
    binding.schema ? foldIdentifier(binding.schema) : null,
    pgPathOf(args),
  );
  if (!tbl) return cols;
  for (const c of tbl.columns) {
    if (c.jsonClassification && c.jsonClassification.kind !== 'not-json') {
      cols.add(c.name.toLowerCase());
    }
  }
  return cols;
}

function collectPgsqlArrayColumns(args: BuildArgs, binding: AliasBinding): Set<string> {
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(binding.relation),
    binding.schema ? foldIdentifier(binding.schema) : null,
    pgPathOf(args),
  );
  return tbl ? collectArrayColumns(tbl.columns) : new Set<string>();
}

function emitAliasColumns(
  out: CompletionCandidate[],
  binding: AliasBinding,
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  path: PgSearchPath,
  text: string,
): void {
  if (binding.kind === 'subquery' || binding.kind === 'derived') {
    // PG table-function column-list (`generate_series(1, 10) AS g(n)`)
    // — the writer-supplied names ARE the synthetic projection. Surface
    // them directly; subquery body inference would either find nothing
    // (the function args are not a SELECT) or pollute the suggestion
    // list with literal-arg names. Closes goals/autocomplete-coverage
    // pgsql script.21 §1 + §3.
    if (binding.syntheticColumns && binding.syntheticColumns.length > 0) {
      for (const name of binding.syntheticColumns) {
        out.push({
          label: name,
          insertText: formatIdentifier(name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `(${binding.alias})`,
        });
      }
      return;
    }
    if (binding.bodyRange) {
      const inferred = inferSubqueryColumns({
        source: text,
        bodyRange: binding.bodyRange,
        engineId: 'pgsql',
        snapshotTables: tables,
        cteBindings,
      });
      for (const name of inferred) {
        out.push({
          label: name,
          insertText: formatIdentifier(name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `(${binding.alias})`,
        });
      }
    }
    return;
  }
  if (binding.kind === 'cte') {
    const relationFolded = foldIdentifier(binding.relation);
    const cte = cteBindings.find((c) => foldIdentifier(c.name) === relationFolded);
    if (!cte) return;
    emitCteColumnList(
      out,
      cte,
      { source: text, engineId: 'pgsql', snapshotTables: tables, cteBindings },
      binding.alias,
    );
    return;
  }
  // Resolve the relation against the snapshot. If the binding has an
  // explicit schema, match it; otherwise walk search_path in order and
  // pick the first hit, falling back to any match if no schema in path
  // owns the relation.
  const relationFolded = foldIdentifier(binding.relation);
  const schemaFolded = binding.schema ? foldIdentifier(binding.schema) : null;
  const tbl = resolveRelation(tables, relationFolded, schemaFolded, path);
  if (tbl) {
    for (const c of tbl.columns) {
      out.push({
        label: c.name,
        insertText: formatIdentifier(c.name),
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `${c.dataType} (${binding.alias})`,
      });
    }
    return;
  }

  // F5.3 Wave-4 — Virtual system-catalog tables. `information_schema` and
  // `pg_catalog` carry tables the introspection bus never lists. When an
  // alias resolves to one of those system tables, surface its static column
  // list. Closes goals/autocomplete-coverage pgsql script.23 §1.
  const sysKey = schemaFolded ? `${schemaFolded}.${relationFolded}` : null;
  const sysCols = sysKey ? PG_SYSTEM_TABLE_COLUMNS[sysKey] : undefined;
  if (sysCols) {
    for (const colName of sysCols) {
      out.push({
        label: colName,
        insertText: formatIdentifier(colName),
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `(${binding.alias})`,
      });
    }
  }
}

/**
 * F5.3 Wave-4 — Postgres virtual system-catalog tables. Each entry maps a
 * fully-qualified `schema.table` to its documented column list. Not in the
 * schema-bus snapshot today; this static map keeps alias-dot completion
 * working without round-tripping pg_catalog introspection. Coverage
 * focuses on the tables actually exercised by the coverage specs — extend
 * as needed.
 */
/**
 * F5.3 Wave-4 — Postgres system-schema table lists. Surfaced at
 * `<schema>.|` dotted-access when the schema is one of the documented
 * system schemas (information_schema, pg_catalog).
 */
const PG_SYSTEM_SCHEMA_TABLES: Readonly<Record<string, readonly string[]>> = {
  information_schema: [
    'tables', 'columns', 'views', 'routines', 'parameters',
    'key_column_usage', 'table_constraints', 'referential_constraints',
    'schemata', 'sequences', 'triggers', 'check_constraints',
    'column_privileges', 'table_privileges', 'usage_privileges',
    'character_sets', 'collations',
  ],
  pg_catalog: [
    'pg_class', 'pg_namespace', 'pg_attribute', 'pg_type', 'pg_enum',
    'pg_proc', 'pg_index', 'pg_constraint', 'pg_database', 'pg_roles',
    'pg_authid', 'pg_settings', 'pg_stats', 'pg_views', 'pg_indexes',
    'pg_tables', 'pg_user', 'pg_locks', 'pg_stat_activity',
    'pg_stat_user_tables', 'pg_stat_user_indexes',
  ],
};

const PG_SYSTEM_TABLE_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  // INFORMATION_SCHEMA — SQL standard views over pg_catalog.
  'information_schema.tables': [
    'table_catalog', 'table_schema', 'table_name', 'table_type',
    'self_referencing_column_name', 'reference_generation',
    'user_defined_type_catalog', 'user_defined_type_schema',
    'user_defined_type_name', 'is_insertable_into', 'is_typed',
    'commit_action',
  ],
  'information_schema.columns': [
    'table_catalog', 'table_schema', 'table_name', 'column_name',
    'ordinal_position', 'column_default', 'is_nullable', 'data_type',
    'character_maximum_length', 'character_octet_length',
    'numeric_precision', 'numeric_precision_radix', 'numeric_scale',
    'datetime_precision', 'interval_type', 'interval_precision',
    'udt_catalog', 'udt_schema', 'udt_name', 'is_identity',
    'identity_generation', 'is_generated', 'is_updatable',
  ],
  'information_schema.views': [
    'table_catalog', 'table_schema', 'table_name', 'view_definition',
    'check_option', 'is_updatable', 'is_insertable_into',
    'is_trigger_updatable', 'is_trigger_deletable',
    'is_trigger_insertable_into',
  ],
  'information_schema.routines': [
    'specific_catalog', 'specific_schema', 'specific_name',
    'routine_catalog', 'routine_schema', 'routine_name', 'routine_type',
    'data_type', 'external_language', 'is_deterministic', 'sql_data_access',
    'security_type',
  ],
  'information_schema.key_column_usage': [
    'constraint_catalog', 'constraint_schema', 'constraint_name',
    'table_catalog', 'table_schema', 'table_name', 'column_name',
    'ordinal_position', 'position_in_unique_constraint',
  ],
  'information_schema.table_constraints': [
    'constraint_catalog', 'constraint_schema', 'constraint_name',
    'table_catalog', 'table_schema', 'table_name', 'constraint_type',
    'is_deferrable', 'initially_deferred', 'enforced',
  ],
  'information_schema.referential_constraints': [
    'constraint_catalog', 'constraint_schema', 'constraint_name',
    'unique_constraint_catalog', 'unique_constraint_schema',
    'unique_constraint_name', 'match_option', 'update_rule', 'delete_rule',
  ],
  // PG_CATALOG — common system tables.
  'pg_catalog.pg_class': [
    'oid', 'relname', 'relnamespace', 'reltype', 'reloftype', 'relowner',
    'relam', 'relfilenode', 'reltablespace', 'relpages', 'reltuples',
    'relallvisible', 'reltoastrelid', 'relhasindex', 'relisshared',
    'relpersistence', 'relkind', 'relnatts', 'relchecks', 'relhasrules',
    'relhastriggers', 'relhassubclass', 'relrowsecurity',
    'relforcerowsecurity', 'relispopulated', 'relreplident', 'relispartition',
    'relrewrite', 'relfrozenxid', 'relminmxid',
  ],
  'pg_catalog.pg_namespace': [
    'oid', 'nspname', 'nspowner', 'nspacl',
  ],
  'pg_catalog.pg_attribute': [
    'attrelid', 'attname', 'atttypid', 'attstattarget', 'attlen', 'attnum',
    'attndims', 'attcacheoff', 'atttypmod', 'attbyval', 'attalign',
    'attstorage', 'attcompression', 'attnotnull', 'atthasdef',
    'atthasmissing', 'attidentity', 'attgenerated', 'attisdropped',
    'attislocal', 'attinhcount', 'attcollation',
  ],
};

/**
 * Resolve a folded relation name to a snapshot table the way PostgreSQL does
 * (`resolvePgRelation`): the qualified `(schema, name)`, else the first
 * search_path schema that owns it — and, only when the path is a guess (it
 * could not be read), the only relation of that name elsewhere. A path read
 * from the server is exact: `FROM orders` with `orders` only off the path
 * names nothing, as on the server.
 */
function resolveRelation(
  tables: ReadonlyArray<SnapshotTable>,
  relationFolded: string,
  schemaFolded: string | null,
  path: PgSearchPath,
): SnapshotTable | undefined {
  return resolvePgRelation(tables, relationFolded, schemaFolded, path);
}

/** The search_path `args` resolves bare relation names through. */
function pgPathOf(args: Pick<BuildArgs, 'searchPath' | 'searchPathExact'>): PgSearchPath {
  return { schemas: args.searchPath, exact: args.searchPathExact };
}


type CteResolveArgs = {
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly cteBindings: readonly CteBinding[];
};

function emitCteColumnList(
  out: CompletionCandidate[],
  cte: CteBinding,
  resolveArgs: CteResolveArgs,
  aliasOrCteName?: string,
): void {
  const cols = resolveCteColumns({ cte, ...resolveArgs });
  if (cols.length === 0) return;
  const detailContext = aliasOrCteName ?? cte.name;
  for (const col of cols) {
    out.push({
      label: col,
      insertText: col,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `Column (${detailContext})`,
    });
  }
}

/**
 * Surface the target's columns at expression positions inside an
 * `INSERT ... ON CONFLICT DO UPDATE` clause. The `excluded` pseudo-table
 * shadows the target's columns in the SET list, so emitting them at
 * tier 1 keeps unqualified column refs (`name = excluded.name`) responsive.
 */
function emitOnConflictTargetColumns(
  out: CompletionCandidate[],
  target: PgsqlInsertTarget,
  tables: ReadonlyArray<SnapshotTable>,
  path: PgSearchPath,
  /** `uniqueOnly` restricts the emission to columns a unique index can
   *  arbitrate on — the CONFLICT-TARGET parens themselves, as opposed to the
   *  DO UPDATE SET / WHERE surfaces, which take the full column set. */
  opts?: { uniqueOnly?: boolean },
): void {
  emitInsertTargetColumns(out, target, tables, path, target.name, opts);
}

/**
 * Emit columns for the resolved INSERT target. The `detailContext` argument
 * lets the caller pin the detail string ("excluded" vs. "<table>") so the
 * suggest widget shows the user-facing alias.
 */
function emitInsertTargetColumns(
  out: CompletionCandidate[],
  target: PgsqlInsertTarget,
  tables: ReadonlyArray<SnapshotTable>,
  path: PgSearchPath,
  detailContext: string,
  opts?: { uniqueOnly?: boolean },
): void {
  // The target as PostgreSQL resolves it (`resolvePgRelation`).
  const match = resolveRelation(
    tables,
    foldIdentifier(target.name),
    target.schema ? foldIdentifier(target.schema) : null,
    path,
  );
  if (!match) return;
  for (const c of match.columns) {
    if (opts?.uniqueOnly && !c.isPrimaryKey && c.isUnique !== true) continue;
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${detailContext})`,
    });
  }
}

/**
 * Emit the columns declared SO FAR in an in-progress CREATE TABLE body — the
 * `ddl-create-table-expression` slot's answer, since the table the caret is
 * defining is not in the snapshot yet. A GENERATED column is dropped at a
 * generation-expression caret: PG rejects a generated column that references
 * another one.
 */
function emitInProgressCreateTableColumns(
  out: CompletionCandidate[],
  columns: readonly InProgressColumn[],
  opts?: { excludeGenerated?: boolean },
): void {
  for (const col of columns) {
    if (opts?.excludeGenerated && col.generated) continue;
    out.push({
      label: col.name,
      insertText: formatIdentifier(col.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: 'In-progress column',
    });
  }
}

/**
 * F5.2-CB2 — Emit the CREATE TRIGGER host table's columns under the
 * synthetic `NEW` / `OLD` pseudo-relation. Used inside trigger function
 * bodies (linked via CREATE TRIGGER … EXECUTE FUNCTION) and inside
 * direct-form trigger bodies.
 */
function emitPgsqlTriggerHostColumns(
  out: CompletionCandidate[],
  host: TriggerHostTable,
  tables: ReadonlyArray<SnapshotTable>,
  pseudo: 'new' | 'old',
): void {
  const nameLower = host.name.toLowerCase();
  const schemaLower = host.schema ? host.schema.toLowerCase() : undefined;
  const match = tables.find((t) => {
    if (t.name.toLowerCase() !== nameLower) return false;
    if (schemaLower) return (t.schema ?? '').toLowerCase() === schemaLower;
    return true;
  });
  if (!match) return;
  const pseudoLabel = pseudo === 'new' ? 'NEW' : 'OLD';
  for (const c of match.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${pseudoLabel} → ${match.schema ?? ''}.${match.name})`,
    });
  }
}

/**
 * JCS Slice C3 — type-aware JSON operator emission. Walks back from the
 * cursor to find the preceding column operand; if it resolves to a
 * JSON-classified column (`native-jsonb` / `native-json`), emits the
 * dialect-correct JSON operator list via SR-RO's
 * `suggestOperatorsForOperand`. Numeric / text / unclassified columns
 * yield no JSON operator candidates — strictly more conservative than
 * the v0 unconditional `emitJsonbOperators` it replaces. At predicate slots
 * (WHERE / HAVING / JOIN ON) the standard comparison operators (`=`, `<`, `>`,
 * `BETWEEN`, `IN`, `IS [NOT] NULL`) for a non-JSON column are emitted by
 * `emitTypeAwareOperators` / `emitTypeAwareExpressionOperators`, type-pruned.
 */
function emitTypeAwareJsonOperators(out: CompletionCandidate[], args: BuildArgs): void {
  const operand = findPrecedingColumnOperand({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    aliasBindings: args.ctx.aliasBindings,
    snapshotTables: args.snapshotTables,
    fold: foldIdentifier,
  });
  if (operand) {
    emitTypeAwareOperators(out, operand.jsonClassification, operand.column.dataType, 'pgsql', args.ctx.positionKind);
    return;
  }
  // Slice 3a — typed expression operand (`x::int ▮`, `CAST(x AS numeric) ▮`).
  const exprType = findPrecedingExpressionType({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    engine: 'pgsql',
    resolveRoutineReturnType: makeRoutineReturnResolver(args.snapshotRoutines, (s) => s.toLowerCase()),
  });
  if (exprType) emitTypeAwareExpressionOperators(out, exprType, 'pgsql', args.ctx.positionKind);
}

function emitPgvectorOperators(out: CompletionCandidate[]): void {
  for (const entry of getPgvectorOperators()) {
    out.push({
      label: entry.op,
      insertText: entry.op,
      isSnippet: false,
      kind: 'snippet',
      tier: 6,
      detail: entry.description,
      filterText: entry.op,
    });
  }
}

/**
 * Emit DECLARE-block variables of an enclosing PL/pgSQL `$$...$$` body as
 * tier-1 column-shaped candidates. The `kind` is `'variable'` so Monaco
 * renders them with the variable icon. Best-effort: the dataType comes
 * from the simple `name TYPE` pattern in `extractDeclareVariables`.
 */
/**
 * JCS Slice C4 — JSON path-hint emission. Detects whether the cursor sits
 * inside a recognised JSON-path string position, reads the preceding column
 * reference's identifier-text directly, finds the owning table in the
 * snapshot, and emits path hints from the per-session sample cache.
 * resolves the LHS column reference against the snapshot honouring FROM/JOIN
 * aliases via the shared resolveColumnRefToTable (bindings come from
 * ctx.aliasBindings, a token-stream scan independent of the tree-sitter tree).
 */
function collectPgJsonPathCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as PostgreSQL resolves it (search_path). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const pos = findJsonPathStringPosition(args.text, args.offset);
  if (!pos) return [];
  const colSegment = readIdentifierBefore(args.text, pos.columnSideEnd);
  if (!colSegment) return [];
  const columnRef = colSegment.parts.join('.');
  const resolved = resolveColumnRefToTable(
    columnRef, args.aliasBindings, args.snapshotTables, foldIdentifier, args.resolveTable);
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  const hintSchema = tbl.schema ?? '';
  // Offer JSON-path hints when the bus classified the column JSON
  // (engine-native / CHECK) OR the user declared it via MetaInfo
  // ("Treat as JSON" — a plain string column the schema bus can't know
  // about). The declaration source is invalidated by the Table Designer
  // toggle, so this reflects changes without a reconnect.
  const busJson =
    !!colMeta.jsonClassification &&
    colMeta.jsonClassification.kind !== 'not-json';
  if (
    !busJson &&
    !isJsonColumnDeclared(
      args.connectionId, args.database, hintSchema, tbl.name, colMeta.name,
    )
  ) {
    return [];
  }
  const cacheEntry = getPathHints(args.connectionId, args.database, {
    engine: 'postgres',
    schema: hintSchema,
    table: tbl.name,
    column: colMeta.name,
  });
  const declaredHints = getJsonPathHints(
    args.connectionId,
    args.database,
    hintSchema,
    tbl.name,
    colMeta.name,
  );
  const out: CompletionCandidate[] = [];
  emitJsonPathCandidates(out, {
    engine: 'postgres',
    cacheEntry,
    declaredHints,
    positionKind: pos.kind,
    partial: pos.partial,
  });
  return out;
}

/**
 * JSONB key-existence completion: `(<col> -> 'a' -> 'b') ? '<key>|'`. The `?`
 * operator tests whether `<key>` exists under the navigated sub-path, so we
 * offer the bare child keys observed there (e.g. for `(metadata -> 'items')`
 * over a structure cache of `$.items[*].sku`, offer `sku`). The shared
 * function/arrow JSON-path detector skips `?` because mysql treats `?` as a
 * parameter placeholder — this slot is therefore postgres-only.
 */
function collectPgJsonbKeyExistenceCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as PostgreSQL resolves it (search_path). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const { text, offset } = args;
  // Walk back to the opening quote of the in-progress string (no newlines).
  let i = offset - 1;
  let quoteStart = -1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '\n') return [];
    if (ch === "'") {
      if (text[i - 1] === "'") { i -= 2; continue; }
      quoteStart = i;
      break;
    }
    i -= 1;
  }
  if (quoteStart < 0) return [];
  const partial = text.slice(quoteStart + 1, offset);
  // The char before the quote (skipping whitespace) must be a lone `?` — the
  // single-key existence operator. `?|` / `?&` take an array literal, not a
  // single key, so leave those to normal completion.
  let j = quoteStart - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j -= 1;
  if (text[j] !== '?' || text[j + 1] === '|' || text[j + 1] === '&') return [];
  const lhs = parsePgJsonbArrowLhs(text, j);
  if (!lhs) return [];
  const resolved = resolveColumnRefToTable(
    lhs.columnRef, args.aliasBindings, args.snapshotTables, foldIdentifier, args.resolveTable);
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  const hintSchema = tbl.schema ?? '';
  const busJson =
    !!colMeta.jsonClassification && colMeta.jsonClassification.kind !== 'not-json';
  if (
    !busJson &&
    !isJsonColumnDeclared(args.connectionId, args.database, hintSchema, tbl.name, colMeta.name)
  ) {
    return [];
  }
  const cacheEntry = getPathHints(args.connectionId, args.database, {
    engine: 'postgres', schema: hintSchema, table: tbl.name, column: colMeta.name,
  });
  const declaredHints = getJsonPathHints(
    args.connectionId, args.database, hintSchema, tbl.name, colMeta.name);
  const allPaths = [
    ...(cacheEntry?.paths ?? []).map((p) => p.path),
    ...(declaredHints ?? []),
  ];
  return emitJsonbSubpathKeys(allPaths, lhs.subpath, partial);
}

/**
 * JSONB containment completion: `<col> @> '{"<key>|":…}'`. The `@>` right
 * operand is a JSON object literal whose keys must exist on `<col>`, so inside
 * that literal — at an object-key position — offer the column's TOP-LEVEL keys
 * (`customer_id`, `status`, `items`). Distinct from the `?` operator (which
 * takes a bare key string, not an object literal) and from the arrow/function
 * path detector (which never sees a `{…}` object literal). Postgres-only.
 */
function collectPgJsonbContainmentCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as PostgreSQL resolves it (search_path). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const { text, offset } = args;
  // Walk back to the opening quote of the in-progress string (no newlines).
  let i = offset - 1;
  let quoteStart = -1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '\n') return [];
    if (ch === "'") {
      if (text[i - 1] === "'") { i -= 2; continue; }
      quoteStart = i;
      break;
    }
    i -= 1;
  }
  if (quoteStart < 0) return [];
  const body = text.slice(quoteStart + 1, offset);
  // The literal must be a JSON object — first non-space char is `{`.
  if (!/^\s*\{/.test(body)) return [];
  // Cursor must be at a KEY position: after the last `{` or `,`, with no `:`
  // yet (a `:` means we've moved past the key into its value).
  const lastSep = Math.max(body.lastIndexOf('{'), body.lastIndexOf(','));
  if (lastSep < 0) return [];
  const keyRegion = body.slice(lastSep + 1);
  if (keyRegion.includes(':')) return [];
  // Partial key = chars typed after an optional opening double-quote.
  const partial = (keyRegion.match(/^\s*"?([^"]*)$/)?.[1]) ?? '';
  // Before the quote (skipping whitespace) must sit the `@>` operator.
  let j = quoteStart - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j -= 1;
  if (text[j] !== '>' || text[j - 1] !== '@') return [];
  // LHS column reference precedes `@>`.
  let k = j - 2;
  while (k >= 0 && /\s/.test(text[k]!)) k -= 1;
  const seg = readIdentifierBefore(text, k + 1);
  if (!seg) return [];
  const resolved = resolveColumnRefToTable(
    seg.parts.join('.'), args.aliasBindings, args.snapshotTables, foldIdentifier, args.resolveTable);
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  const hintSchema = tbl.schema ?? '';
  const busJson =
    !!colMeta.jsonClassification && colMeta.jsonClassification.kind !== 'not-json';
  if (
    !busJson &&
    !isJsonColumnDeclared(args.connectionId, args.database, hintSchema, tbl.name, colMeta.name)
  ) {
    return [];
  }
  const cacheEntry = getPathHints(args.connectionId, args.database, {
    engine: 'postgres', schema: hintSchema, table: tbl.name, column: colMeta.name,
  });
  const declaredHints = getJsonPathHints(
    args.connectionId, args.database, hintSchema, tbl.name, colMeta.name);
  const allPaths = [
    ...(cacheEntry?.paths ?? []).map((p) => p.path),
    ...(declaredHints ?? []),
  ];
  // Empty sub-path → the immediate children of `$` (the top-level keys).
  return emitJsonbSubpathKeys(allPaths, [], partial);
}

/**
 * Parse the left operand of a JSONB `?` into a base column reference + the
 * sequence of `-> 'key'` navigations. Handles `(col -> 'a' -> 'b')` and the
 * bare `col` form. Returns null when the operand isn't a column/arrow chain.
 */
function parsePgJsonbArrowLhs(
  text: string,
  beforeQuestion: number,
): { columnRef: string; subpath: string[] } | null {
  let i = beforeQuestion - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i -= 1;
  if (text[i] !== ')') {
    const seg = readIdentifierBefore(text, i + 1);
    return seg ? { columnRef: seg.parts.join('.'), subpath: [] } : null;
  }
  // Parenthesised arrow chain — find the matching `(`.
  let depth = 0;
  let k = i;
  while (k >= 0) {
    if (text[k] === ')') depth += 1;
    else if (text[k] === '(') { depth -= 1; if (depth === 0) break; }
    k -= 1;
  }
  if (k < 0) return null;
  const inner = text.slice(k + 1, i);
  const segs = inner.split(/->>?/);
  const columnRef = segs[0]!.trim();
  if (!columnRef) return null;
  const subpath: string[] = [];
  for (let s = 1; s < segs.length; s += 1) {
    const m = segs[s]!.trim().match(/^'((?:[^']|'')*)'$/);
    if (!m) return null;
    subpath.push(m[1]!.replace(/''/g, "'"));
  }
  return { columnRef, subpath };
}

/**
 * From the column's structure-cache paths, emit the bare child keys that live
 * one level under `$.<subpath>` (descending through an array element when the
 * sub-path value is an array). `$.items[*].sku` under sub-path `items` yields
 * the key `sku`.
 */
function emitJsonbSubpathKeys(
  paths: readonly string[],
  subpath: readonly string[],
  partial: string,
): CompletionCandidate[] {
  const prefix = `$${subpath.map((k) => `.${k}`).join('')}`;
  const keys = new Set<string>();
  for (const path of paths) {
    if (!path.startsWith(prefix)) continue;
    let rest = path.slice(prefix.length);
    // Descend through an array element marker if present (`[*]` / `[0]`).
    const arr = rest.match(/^\[[^\]]*\]/);
    if (arr) rest = rest.slice(arr[0].length);
    if (rest.startsWith('.')) rest = rest.slice(1);
    if (!rest) continue;
    // The immediate child key only (stop at the next `.` / `[`).
    const key = rest.split(/[.[]/)[0]!;
    if (key) keys.add(key);
  }
  const out: CompletionCandidate[] = [];
  for (const key of [...keys].sort()) {
    const insertText =
      partial.length > 0 && key.startsWith(partial) ? key.slice(partial.length) : key;
    out.push({
      label: key,
      insertText,
      isSnippet: false,
      kind: 'snippet',
      tier: 6,
      detail: 'json key',
      filterText: key,
    });
  }
  return out;
}

/**
 * Phase 3 — enum-value completion. Detects whether the cursor sits at a
 * comparison value operand (`WHERE col = '|`, `col IN ('|`, `col <> '|`),
 * resolves the LHS column reference against the snapshot honouring FROM/JOIN
 * aliases via the shared resolveColumnRefToTable (bindings come from
 * ctx.aliasBindings, a token-stream scan independent of the tree-sitter tree),
 * and emits the column's cached distinct values. Returns [] when the cursor
 * isn't at a value operand or the column has no cached enum values.
 *
 * Path-aware extension (MERGE-2): when the LHS of the comparison is a PG
 * JSON-extract expression (`col->>'key'`, `col#>>'{a,b}'`, etc.), the
 * resolved canonical path is passed to `emitEnumValueCandidates` so the
 * path-keyed MetaInfo enum cache is consulted instead of the column-level
 * cache. Wildcard probe is handled inside `emitEnumValueCandidates`.
 */
function collectPgEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as PostgreSQL resolves it (search_path). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  // Table DDL carries no queried relation, so a bare column reference here
  // must not fall back to an unrelated snapshot table's cached values
  // (see `isInsideTableDdlStatement`). Closes oracle script.29 §4.
  if (isInsideTableDdlStatement(args.text, args.offset)) return [];
  const cmp = detectComparisonValueContext(args.text, args.offset);
  if (!cmp) {
    // detectComparisonValueContext misses JSON-chain LHS (col->>'a' = …) because
    // the char immediately before `=` is the closing quote of the path literal,
    // not part of an identifier. Try the JSON-path extractor directly against
    // each candidate column; the extractor's own findComparisonOpEndBefore gate
    // keeps this cheap — it returns null immediately when the cursor isn't at a
    // comparison RHS.
    return collectPgJsonChainEnumValueCandidates(args);
  }

  // Resolve the LHS column reference. For plain column refs this is the
  // direct binding; for JSON-extract expressions the column ref is the
  // base column (e.g. `metadata` in `metadata->>'status'`).
  const resolved = resolveColumnRefToTable(
    cmp.columnRef, args.aliasBindings, args.snapshotTables, foldIdentifier, args.resolveTable);
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;

  // Try to extract a JSON path from the cursor context (PG-specific).
  // `extractJsonPathFromCursorPgsql` looks at the LHS of the comparison
  // operator for `->>` / `#>>` expressions and returns the canonical path.
  const pathResult = extractJsonPathFromCursorPgsql({
    text: args.text,
    cursorOffset: args.offset,
    column: colMeta.name,
  });

  const out: CompletionCandidate[] = [];
  emitEnumValueCandidates(out, {
    connectionId: args.connectionId,
    database: args.database,
    schema: tbl.schema ?? '',
    table: tbl.name,
    column: colMeta.name,
    // Pass the canonical path when a JSON-extract expression was detected.
    path: pathResult?.canonicalPath,
    // Bare insert when the cursor is already inside a `'…` literal so the
    // existing quotes aren't duplicated (range handled by computeEnumValueEdit).
    quote: findValueStringBodyStart(args.text, args.offset) === undefined,
    // Native pg_enum members surface when the column isn't a declared MetaInfo
    // enum. Only the column-level lookup — a JSON-extract LHS is not an enum.
    nativeFallbackMembers: pathResult ? undefined : colMeta.enumValues,
  });
  return out;
}

/**
 * Fallback for JSON-chain LHS comparisons that `detectComparisonValueContext`
 * misses (e.g. `col->>'key' = '|'`). Iterates over every column visible via
 * the alias bindings and snapshot tables, runs the PG JSON-path extractor for
 * each, and emits enum-value candidates for the first match.
 *
 * The extractor's own `findComparisonOpEndBefore` gate makes this cheap: it
 * returns `null` immediately when the cursor is not at a comparison RHS, so
 * the loop exits fast in non-matching positions.
 */
function collectPgJsonChainEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as PostgreSQL resolves it (search_path). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const quote = findValueStringBodyStart(args.text, args.offset) === undefined;

  // The extractor's backward scan never depends on the column (it is only
  // compared at the end) — scan once, then match the column it names. This
  // fallback runs on nearly every request; one scan per snapshot column was
  // ~20% of provider time on a 600-table schema (1.5.1 F2).
  const pathResult = extractJsonPathFromCursorPgsql({ text: args.text, cursorOffset: args.offset });
  if (!pathResult) return [];
  const targetColumn = pathResult.column.toLowerCase();
  // The statement's own relations (as PostgreSQL resolves them) first, then
  // the first snapshot table owning the column.
  for (const tbl of boundTablesFirst(args.aliasBindings, args.snapshotTables, args.resolveTable)) {
    for (const col of tbl.columns) {
      if (col.name.toLowerCase() !== targetColumn) continue;

      // Found a JSON-chain match. Emit enum values for this column + path.
      const out: CompletionCandidate[] = [];
      emitEnumValueCandidates(out, {
        connectionId: args.connectionId,
        database: args.database,
        schema: tbl.schema ?? '',
        table: tbl.name,
        column: col.name,
        path: pathResult.canonicalPath,
        quote,
      });
      if (out.length > 0) return out;
    }
  }
  return [];
}

/**
 * Read a 1–3-part dotted identifier ending at `offset`. Tolerates surrounding
 * whitespace. Returns `null` if no identifier is visible to the cursor's left.
 */
function readIdentifierBefore(
  text: string,
  offset: number,
): { readonly parts: readonly string[] } | null {
  let i = offset;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  const parts: string[] = [];
  while (i > 0) {
    const end = i;
    while (i > 0 && /[A-Za-z0-9_"$]/.test(text[i - 1]!)) i -= 1;
    if (i === end) break;
    parts.unshift(text.slice(i, end));
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
    if (text[i - 1] !== '.') break;
    i -= 1;
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  }
  return parts.length > 0 ? { parts } : null;
}

// Test-only export so unit tests can exercise the schema-aware emission
// without standing up the full Monaco translation.
export function __testEmitTablesAndSchemas(
  tables: ReadonlyArray<SnapshotTable>,
  schemas: readonly string[],
  searchPath: readonly string[] = PG_DEFAULT_SEARCH_PATH,
): CompletionCandidate[] {
  const out: CompletionCandidate[] = [];
  emitTablesAndSchemas(out, tables, schemas, searchPath);
  return out;
}

export function __testNeedsDoubleQuote(s: string): boolean {
  return needsDoubleQuote(s);
}

export function __testDoubleQuote(s: string): string {
  return doubleQuote(s);
}

export function __testUnquote(s: string): string {
  return unquoteIdentifier(s);
}

